import { useCallback, useEffect, useMemo, useReducer, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import {
  Button,
  Chips,
  Choice,
  DataTable,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  Icon,
  Input,
  Pagination,
  Panel,
  Select,
  Stat,
  Tabs,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
  type ErrorDetail,
} from '@dayon/design-system';
import { formatCount, formatPesos, formatPesosCompact, parsePesos } from '@dayon/money';
import type {
  AdjustmentRow,
  DepositRetryAttemptRow,
  FundsSummary,
  PageMeta,
  PayoutProviderStatus,
  PlayerRow,
  ReleaseOutcome,
  TransactionRow,
  WithdrawalRow,
} from '@dayon/contracts';
import { DEPOSIT_RETRY_COOLDOWN_MINUTES, PARKED_REASON_WORDS } from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { auditSubjectWords, codeAsWords } from '../../lib/audit-words.ts';
import { useSession } from '../../lib/session.tsx';
import { formatWait } from '../../lib/wait.ts';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { OtherRequests } from './OtherRequests.tsx';
import { ParkedNotifications } from './ParkedNotifications.tsx';
import { PayoutAccountsReview } from './PayoutAccountsReview.tsx';
import { PayoutPanel } from './PayoutPanel.tsx';
import { PayoutStop } from './PayoutStop.tsx';
import {
  PHASE_LABEL,
  PHASE_TONE,
  byParkedAt,
  clock,
  healthLine,
  holdWords,
  nameMatchCheck,
  payoutLabel,
  payoutTone,
  phaseOf,
  providerFigure,
  stamp,
  tellOutcome,
  type Phase,
} from './payout-state.ts';
import { STATUS_LABEL, TIER_LABEL } from '../players/vocabulary.ts';
import { allBrandsPhrase } from '../../lib/brand-words.ts';

/**
 * Player funds — the money-operations queue.
 *
 * Three tabs over one header, exactly as the design frames it: the
 * maker-checker payout queue, inbound rail activity, and the credit/debit
 * queue — and a fourth the payout rails added, the registered payout accounts
 * whose holder's name did not match the player's ID and wait for a second
 * person (PayoutAccountsReview.tsx). What differs from the design is what the
 * platform can actually answer, and that difference is stated on the screen
 * rather than filled in.
 *
 * THE DATA SUPPLY, in full:
 *   GET  /api/withdrawals        payment.withdrawal, joined to player, brand, rail and the names of
 *                                its maker and checker; with `ref`, one request whatever its
 *                                status — the drawer reads its row again after every action with
 *                                it, and a link to a withdrawal (`?withdrawal=WDL-…`) opens one;
 *                                with `playerId`, one player's requests newest first — the
 *                                drawer's list of the player's other requests (OtherRequests.tsx)
 *   GET  /api/adjustments        wallet.manual_adjustment, the checker queue
 *   GET  /api/players/:id        the player behind the request in the drawer
 *   GET  /api/dashboard/live     the pending-withdrawal aggregate
 *   GET  /api/funds/summary      released today, deposits today, my checker queue
 *   GET  /api/transactions       wallet.ledger_transaction, filtered by kind
 *   GET  /api/deposits/retry-attempts  payment.deposit_retry_attempt, per ledger row
 *   GET  /api/platform/audit     platform.audit_log, for the fund audit trail
 *   GET  /api/payments/errors    payment.payment_error_code, for a failed deposit
 *   GET  /api/withdrawals/:id/payout  a released payout's StarPay record (PayoutPanel.tsx)
 *   GET  /api/payouts/provider   the driver and StarPay's connection check, for the Payout provider
 *                                tile; and the same-id re-send flag, for one parked reason (PayoutPanel.tsx)
 *   GET  /api/payout-accounts/review  the accounts waiting for a second person: the header's count
 *                                     reads one row of it on mount, the tab (PayoutAccountsReview.tsx) the list
 *
 * THE WRITES, in full:
 *   POST /api/withdrawals/maker         signs the MAKER half of a selection
 *   POST /api/withdrawals/:id/release   posts the payout, stamps the checker and, on a
 *                                       StarPay rail, hands the transfer to StarPay
 *   POST /api/withdrawals/:id/reject    closes it with a reason code
 *
 * The first of those three is the one worth reading twice. It reads like an
 * approval and does close to the opposite: `withdrawal_maker_is_not_checker`
 * means a payout's maker can never release it, so signing a selection as maker
 * HANDS THOSE PAYOUTS TO SOMEBODY ELSE rather than advancing them. The dialog
 * says so before the press and the route answers with how many other admins can
 * release, because an operator alone on duty has just parked every row and no
 * other screen on this platform would tell them.
 *   POST /api/adjustments               raises a credit/debit request (funds.adjust)
 *   POST /api/adjustments/:id/approve   posts the ledger movement (funds.approve)
 *   POST /api/adjustments/:id/reject    closes it with a reason (funds.approve)
 *   POST /api/deposits/:txnId/retry-attempts  RECORDS an attempt. No provider call.
 *   POST /api/withdrawals/:id/payout/unpark   an operator's action on a parked payout (PayoutActionDialog.tsx)
 *   POST /api/payouts/breaker/reset           restarts payouts after StarPay refused the credentials (PayoutStop.tsx)
 *   POST /api/payout-accounts/:id/review      accepts or refuses an account, with a reason (PayoutAccountsReview.tsx)
 *
 * PAYOUT EXECUTION EXISTS ON ONE KIND OF RAIL, and the screen says which.
 * Releasing a payout on a StarPay rail posts the ledger movement and hands the
 * transfer to StarPay in the same action; the release answers with a sentence
 * that says what StarPay answered (`payout.sentence`), and the screen repeats it
 * rather than composing a claim of its own — in a toast when the payout went
 * through, and in the error dialog when StarPay refused it, parked it or has not
 * answered (`tellOutcome`). Every other rail still records the
 * payout and a person sends the money — the same sentence says so, in the words
 * the old toast used. A payout the platform stopped acting on waits under the
 * "Waiting for an operator" chip, and the drawer's Payout panel carries
 * StarPay's own fields and the operator's actions on it — asking StarPay,
 * confirming it paid, refunding it, re-sending it under the same id, and closing
 * the hold on one whose money is settled. After any action from the drawer it
 * reads its row again, so what it shows is where the request stands now. The
 * KPI band's provider tile counts both, says which driver answers and whether
 * StarPay answered its connection check, and the stop StarPay's refusal of the
 * credentials trips is said under the band (PayoutStop.tsx).
 * Deposit retry is half-wired ON PURPOSE and the screen says which half: the
 * ATTEMPT is recorded with who and why, and the money does not move. Nothing
 * here toasts a success over a request that was never sent.
 *
 * THERE IS NO REVEAL CONTROL, and that is a decision, not a gap. The full
 * account number exists now — encrypted in the payout-account vault
 * (services/api/src/modules/player-money/payout-vault.ts), written when the
 * player registers the account — and the platform reads it at exactly one
 * moment, the hand-over to StarPay, recording every read in
 * player.payout_account_access. No screen shows it: a number a console can
 * display is a number a console can leak, and what a checker actually compares
 * is the holder's name, which the checks report. A request raised before
 * registered accounts kept only its last four digits, and the drawer says that
 * instead.
 */

/* ── Wire shapes ───────────────────────────────────────────────────────────
   snake_case where the handler answers with a raw SELECT list, camelCase where
   a zod contract in @dayon/contracts shapes the response. */

interface LivePayload {
  live: {
    depositsLastHour: string;
    depositCountLastHour: number;
    withdrawalsPending: string;
    withdrawalCountPending: number;
    oldestPendingMinutes: number | null;
  };
  queue: {
    withdrawalsAwaitingChecker: {
      count: number;
      value: string;
      oldestMinutes: number | null;
      breachingSla: number;
    };
  };
}

/**
 * The few of /api/players/:id's columns this screen reads: the player's own, as the route's
 * SELECT names them, and the joined brand name and the text-cast balance.
 */
interface PlayerRecord {
  id: string;
  ref: string;
  display_name: string;
  status: string;
  kyc_status: string;
  tier: string;
  risk_score: number;
  brand_name: string;
  cash_balance: string;
  registered_at: string;
}

interface PlayerDetail {
  player: PlayerRecord;
}

export interface AuditEntry {
  seq: string;
  actor_label: string;
  action: string;
  subject_type: string;
  subject_id: string | null;
  created_at: string;
}

interface AuditPayload {
  entries: AuditEntry[];
  /**
   * Whose entries were read. Null: the reader's access covers every brand, and the newest entries
   * of the whole log were read. Otherwise the names of the brands the reader is restricted to, and
   * only entries recorded against one of them were read — none recorded against no brand.
   */
  restrictedTo: string[] | null;
  chainBreaks: unknown[];
  intact: boolean;
}

interface ErrorCodeRow {
  code: string;
  provider: string;
  meaning: string;
  retry_safe: boolean;
}

/**
 * A `platform.setting` row off `GET /api/platform/settings`.
 *
 * `brand` is the brand NAME on an override row and null on the global row —
 * that is what the handler selects, and it is how a per-brand value is resolved
 * below.
 */
interface PlatformSettingRow {
  key: string;
  value: string;
  per_brand: boolean;
  description: string | null;
  brand: string | null;
}

/**
 * How a supporting read went.
 *
 * "still loading", "the read failed", "the caller may not make it" and "it came
 * back empty" are four different facts, and a screen that collapses them tells
 * the operator something untrue about at least three of them.
 */
type ReadState = 'loading' | 'ready' | 'failed' | 'forbidden';

/* ── Vocabulary ──────────────────────────────────────────────────────────── */

const TABS = [
  { key: 'withdrawals', label: 'Withdrawals' },
  { key: 'deposits', label: 'Deposits' },
  { key: 'adjustments', label: 'Manual adjustments' },
  { key: 'payout-accounts', label: 'Payout accounts' },
  /* The StarPay notifications filed for a person: the payout desk's (ruling 173), opened from the
     Payout provider tile, which counts the ones still waiting. */
  { key: 'parked-notifications', label: 'Parked notifications' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const isTabKey = (value: string): value is TabKey => TABS.some((t) => t.key === value);

/** Open items only. A closed request opens the drawer read-only. */
const OPEN_PHASES = new Set<Phase>(['AWAITING_MAKER', 'AWAITING_CHECKER', 'HELD_BY_RISK']);

/**
 * The stage filter, mapped to what the endpoint can actually be asked.
 *
 * `withdrawalQueueQuery` takes ONE optional status. With none it returns the
 * working queue — AWAITING_CHECKER plus HELD_BY_RISK — so there is no "all
 * statuses" request to make, and the first chip says what it really is.
 *
 * PARKED is not a status. A payout waiting for an operator was RELEASED (or
 * refunded) and the platform stopped acting on it, so no status chip would ever
 * find it; the queue's own `parked` flag lists them, oldest park first.
 */
const STAGE_FILTERS = [
  { key: 'QUEUE', label: 'In queue', status: null },
  { key: 'AWAITING_CHECKER', label: 'Awaiting checker', status: 'AWAITING_CHECKER' },
  { key: 'HELD_BY_RISK', label: 'Held by risk', status: 'HELD_BY_RISK' },
  { key: 'RELEASED', label: 'Released', status: 'RELEASED' },
  { key: 'REJECTED', label: 'Rejected', status: 'REJECTED' },
  { key: 'FAILED', label: 'Failed', status: 'FAILED' },
  { key: 'PARKED', label: 'Waiting for an operator', status: null, parked: true },
] as const;

type StageKey = (typeof STAGE_FILTERS)[number]['key'];

/**
 * The deposits tab's settlement filter.
 *
 * Added because the Retry control was unreachable without it. The ledger holds
 * ~9,900 DEPOSIT transactions and six of them are FAILED, scattered over pages
 * 217 to 394 — so an operator looking for a failed deposit to act on had to
 * page through the whole ledger to find one. A control nobody can reach is not
 * a control.
 *
 * Server-side, through `/api/transactions?status=`, which the endpoint already
 * accepted; it is NOT a filter over the loaded page, and the caption says so.
 * REVERSED and AWAITING_CHECKER are omitted deliberately: they are values of
 * the shared transaction status that a DEPOSIT does not take on this platform,
 * and a chip that can only ever produce an empty table is a filter that reads
 * as a broken screen.
 */
const DEPOSIT_FILTERS = [
  { key: 'ALL', label: 'All', status: null },
  { key: 'SETTLED', label: 'Settled', status: 'SETTLED' },
  { key: 'PENDING', label: 'Pending', status: 'PENDING' },
  { key: 'FAILED', label: 'Failed', status: 'FAILED' },
] as const;

type DepositFilterKey = (typeof DEPOSIT_FILTERS)[number]['key'];

type SortKey = 'waiting' | 'amount';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 25;

/**
 * The adjustment dialog's player list.
 *
 * 200 is the hard maximum `pageQuery` allows (`packages/contracts/src/envelope.ts`),
 * so this is ONE PAGE and never the roster — the seed alone creates ~2,010
 * players. The field says so, and carries a server-side search so a player
 * outside the page is still reachable.
 */
const PLAYER_OPTION_PAGE = 200;

/* Times read "29 Aug, 21:12" — 24-hour, so a ledger read at 09:14 is unambiguous — in the
   funds screen's one format (`stamp`, payout-state.ts), which the Payout panel shares; a time of
   day alone reads "21:12" (`clock`, beside it). */

/** Banded exactly as the players list and the profile band it. */
const riskBand = (score: number): string => (score >= 70 ? 'High' : score >= 40 ? 'Medium' : 'Low');

/** Payout ratio, as the design writes it: two decimals, not a percentage. */
const formatRatio = (bp: number): string => (bp / 10000).toFixed(2);

/**
 * A rate in basis points, as a percentage: 137 → "1.37%".
 *
 * Basis points on the wire and a percentage on screen, because the integer is
 * what survives JSON without rounding and the percentage is what an operator
 * reads. Two decimals, so a rail failing three deposits in ten thousand does
 * not round to a clean 0%.
 */
const formatRatePercent = (bp: number): string => `${(bp / 100).toFixed(2)}%`;

/**
 * The hold threshold is a SETTING, not a constant.
 *
 * `platform.setting` carries `withdrawal.payout_ratio_hold` with
 * `per_brand = true`, so every brand may override it and 0.90 is only the value
 * the seed writes on the global row. It is read from
 * `GET /api/platform/settings` below and resolved per row; where it cannot be
 * read the ratio is shown unbanded rather than compared against a number
 * hardcoded here, because banding a payout decision against a threshold no
 * brand configured is the same class of error as inventing the number itself.
 */
const PAYOUT_RATIO_HOLD_KEY = 'withdrawal.payout_ratio_hold';

/** `'0.90'` → 9000 basis points. null for anything that is not a finite ratio. */
function ratioToBp(value: string): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 10000) : null;
}

interface HoldThreshold {
  bp: number;
  /** Whether the value came from this brand's override or the global row. */
  scope: 'brand' | 'global';
}

/**
 * What the Ratio column actually is — corrected.
 *
 * `/api/withdrawals` recomputes it on every read from two LATERAL sub-selects
 * over CURRENT lifetime data — `sum(payment.deposit WHERE status='SETTLED')` and
 * `sum(payment.withdrawal WHERE status='RELEASED')` — aliased `AS
 * payout_ratio_bp`. The stored `payment.withdrawal.payout_ratio_bp` column is
 * never in the SELECT list and the seed never writes it, so it is always NULL.
 * Nothing about this value is snapshotted, and the screen must not say it is:
 * a deposit settled after the request moves it, and on a closed request the
 * figure shown is the one as of now, not the one the hold was taken on.
 */
const RATIO_PROVENANCE =
  'Everything ever paid out to this player, divided by everything they ever put in. Worked out fresh each time this screen is opened, so a deposit that settled after the request moves it — on a closed request you are reading today\u2019s figure, not the one the decision was made on.';

const KYC_LABEL: Record<string, string> = {
  UNVERIFIED: 'Unverified',
  PENDING: 'Pending',
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
};

/**
 * Risk-alert severity, spelled the way the Risk module spells it.
 *
 * The same four words in the same case, deliberately: an operator who reads
 * "Critical" here and opens the Risk module must not have to work out whether
 * "Severe" is the same thing.
 */
const SEVERITY_LABEL: Record<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL', string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};

const TXN_STATUS_LABEL: Record<TransactionRow['status'], string> = {
  SETTLED: 'Settled',
  PENDING: 'Pending',
  AWAITING_CHECKER: 'Awaiting checker',
  FAILED: 'Failed',
  REVERSED: 'Reversed',
};

/**
 * The five reason codes the design offers, as the closed set the contract now
 * enforces (`adjustmentReasonCode` in @dayon/contracts).
 *
 * The code is what is stored and what a finance report groups by; the label is
 * what an operator reads. `manual_adjustment.reason_code` is still a free
 * varchar with no reference table and no CHECK, so the zod enum on the request
 * is the ONLY thing keeping the column to these five — which docs/RUNNING.md
 * states for engineers. It is not an operator's fact, so the screen no longer
 * carries it.
 */
const REASON_CODES = [
  { code: 'GOODWILL', label: 'Goodwill — service recovery' },
  { code: 'FAILED_DEPOSIT', label: 'Failed deposit — manual credit' },
  { code: 'ROUND_VOID', label: 'Game round void' },
  { code: 'BONUS_CORRECTION', label: 'Bonus correction' },
  { code: 'CHARGEBACK', label: 'Chargeback / reversal' },
] as const;

/**
 * Why a checker refuses an adjustment. A different vocabulary from the reasons
 * one is raised for: refusing is not adjusting, and a report that mixed the two
 * would count a rejected goodwill credit as goodwill paid.
 */
const ADJUSTMENT_REJECTION_REASONS = [
  { code: 'INSUFFICIENT_EVIDENCE', label: 'Insufficient evidence' },
  { code: 'WRONG_PLAYER', label: 'Raised against the wrong player' },
  { code: 'WRONG_AMOUNT', label: 'Amount is wrong' },
  { code: 'DUPLICATE_REQUEST', label: 'Duplicate request' },
  { code: 'POLICY_BREACH', label: 'Outside adjustment policy' },
  { code: 'RAISED_IN_ERROR', label: 'Raised in error' },
] as const;

/** Codes a queue row may carry, mapped back to the label they were raised as. */
const REASON_LABEL: Record<string, string> = Object.fromEntries(
  REASON_CODES.map((r) => [r.code, r.label]),
);

/** Subject types a fund event would be filed under in platform.audit_log. */
const FUND_SUBJECTS = new Set([
  'withdrawal',
  'manual_adjustment',
  'deposit',
  'ledger_transaction',
  // Recording a retry attempt is a fund event and belongs in the fund trail:
  // it names an operator, a deposit and a reason, and an auditor reading this
  // dialog is reading for exactly that.
  'deposit_retry_attempt',
  // Restarting payouts after StarPay refused the credentials is pressed on this
  // screen and puts every brand's payouts back in motion, with a reason; its
  // row is filed against the provider, not a withdrawal (PAYOUT_BREAKER_RESET).
  'payout_provider',
  // A person's verdict on a payout account whose holder's name did not match the
  // player's ID is given on this screen's Payout accounts tab, with a reason, and
  // is filed against the ACCOUNT (PAYOUT_ACCOUNT_REVIEWED) — under none of the
  // subjects above, so without this line every verdict was off a trail whose
  // notice says a fund event that is not there was never performed. The player's
  // own registering and retiring of an account (PAYOUT_ACCOUNT_REGISTERED,
  // PAYOUT_ACCOUNT_RETIRED) share the subject and come with it on purpose: they
  // are what the verdict is a verdict ON, and an auditor asking why money went to
  // an account reads the whole story in one place.
  'payout_account',
  // Marking a StarPay notification that names no payout as seen is pressed on this
  // screen's Parked notifications tab, with a reason, and is filed against the
  // notification (WEBHOOK_INBOX_SEEN) — under none of the subjects above.
  'payout_notification',
]);

/*
 * The refusal reasons, mirroring `withdrawalRejectionReason` in the contract.
 *
 * A closed set on both sides: the code drives what the player is told and the
 * report that counts refusals by cause, and neither can group free text. The
 * database refuses a REJECTED row with no code at all.
 */
const REJECTION_REASONS = [
  { code: 'KYC_INCOMPLETE', label: 'KYC incomplete' },
  { code: 'RAIL_MISMATCH', label: 'Payout account does not match the record' },
  { code: 'SUSPECTED_FRAUD', label: 'Suspected fraud' },
  { code: 'BONUS_WAGERING_UNMET', label: 'Bonus wagering not met' },
  { code: 'DUPLICATE_REQUEST', label: 'Duplicate request' },
  { code: 'PLAYER_REQUEST', label: 'Withdrawn at the player’s request' },
] as const;

/*
 * Why an operator is recording a deposit retry attempt, mirroring
 * `depositRetryReason` in the contract.
 *
 * Closed on both sides for the same reason the refusal reasons are: the code is
 * what a report groups on, and prose cannot be grouped. Note what none of them
 * says — none of them claims the deposit was re-submitted, because it was not.
 */
const RETRY_REASONS = [
  { code: 'PROVIDER_TIMEOUT', label: 'Provider timed out — no verdict returned' },
  { code: 'PROVIDER_ADVISED_RETRY', label: 'Provider advised a retry' },
  { code: 'RAIL_RECOVERED', label: 'Rail was down and has recovered' },
  { code: 'PLAYER_CONFIRMED_DEBIT', label: 'Player confirms they were debited' },
  { code: 'MANUAL_RECONCILIATION', label: 'Reconciling against the provider statement' },
] as const;

/* ── A check row in the review drawer ────────────────────────────────────── */

type CheckState = 'ok' | 'attention' | 'unknown';

interface CheckRow {
  key: string;
  label: string;
  value: ReactNode;
  state: CheckState;
  note?: string;
}

/** Three states, three glyphs, all from the icon set the design export uses. */
const CHECK_ICON: Record<CheckState, string> = {
  ok: 'check-circle',
  attention: 'warning-circle',
  unknown: 'info',
};

/**
 * THE NOTE UNDER "OPEN RISK ALERTS", for a reader who cannot open the Risk module.
 *
 * A PERMISSION KEY STAYS ON SCREEN: a reader who lacks one is told the key and who to ask, as every
 * other screen in this console tells them. A Finance Lead or an Auditor sees how many alerts are
 * open and the worst severity, but not which rule fired, because that is the Risk module and
 * players.restrict is what opens it. The key is on the permission matrix and their role holds it or
 * does not, so "it needs players.restrict" is an errand somebody can run — quoted to whoever grants
 * it, or to the Risk Officer they ask instead.
 *
 * This note used to leave the key out, on the reasoning that a checker cannot grant themselves
 * anything, and the funds screen's "Not yet available" panel said it instead. When that panel went,
 * the screen named the key nowhere. Constraint, table and route names still stay off it.
 *
 * The drawer picks which note to show, and this takes the role alone. Passing it the flag from
 * `allowed()` as well made oxlint's React Compiler rules report the Sign as maker callback as
 * memoised wrongly (two `preserve-manual-memoization` errors, which fail the lint step): the
 * compiler cannot tell a hook's answer is a plain boolean, so a call it is passed to might change it.
 */
export function alertsNoteWithoutKey(role: string): string {
  return `You can see how many and how serious, but not which rule fired — the Risk module shows that, and it needs players.restrict, which ${role} does not hold. An open alert does not stop the release on its own; the hold below is what does that. If the severity worries you, ask a Risk Officer before you release.`;
}

/* ── The stage, on the queue and in the drawer ─────────────────────────── */

/** Where a request stands in the maker-checker flow, as a tag — one drawing for the queue and the drawer. */
export function StageTag({ row }: { row: WithdrawalRow }) {
  const phase = phaseOf(row);
  return <Tag tone={PHASE_TONE[phase]}>{PHASE_LABEL[phase]}</Tag>;
}

/* ── The fund audit trail, in words ──────────────────────────────────────── */

/**
 * THE TRAIL'S REFERENCE AND ACTION COLUMNS PRINT WORDS. The log stores a subject as
 * `payout_account` and an action as `PAYOUT_ACCOUNT_REVIEWED`; neither is anything an operator
 * reads, so each is said as what it is — "Payout account", "Payout account reviewed". A subject's
 * words are the console's one map (`AUDIT_SUBJECT_WORDS`, lib/audit-words.ts), which Recent actions
 * on Access control reads too; every subject the trail keeps (`FUND_SUBJECTS` above) has words of
 * its own there, which payout-review-tab.test.tsx checks for each subject the trail keeps. Below,
 * what each fund event is called, for the Action column.
 */
const FUND_ACTION_WORDS: Readonly<Record<string, string>> = {
  WITHDRAWAL_REQUESTED: 'Withdrawal requested',
  WITHDRAWAL_HELD_BY_RISK: 'Withdrawal held by risk',
  WITHDRAWAL_RISK_HOLD_LIFTED: 'Withdrawal risk hold lifted',
  WITHDRAWAL_MAKER_RECORDED: 'Withdrawal signed by its maker',
  WITHDRAWAL_RELEASED: 'Withdrawal released',
  WITHDRAWAL_REJECTED: 'Withdrawal refused',
  WITHDRAWAL_SENT: 'Payout sent to StarPay',
  WITHDRAWAL_PAID: 'Payout paid',
  WITHDRAWAL_PARKED: 'Payout held for an operator',
  /* Cleared by an operator's action, or by StarPay confirming the payout: the Actor column says which. */
  WITHDRAWAL_UNPARKED: 'Payout hold cleared',
  WITHDRAWAL_PAYOUT_FAILED: 'Payout failed and refunded',
  WITHDRAWAL_REVERSED: 'Payout returned by the bank and refunded',
  ADJUSTMENT_RAISED: 'Adjustment raised',
  ADJUSTMENT_APPROVED: 'Adjustment approved',
  ADJUSTMENT_REJECTED: 'Adjustment refused',
  DEPOSIT_STARTED: 'Deposit started',
  DEPOSIT_SETTLED: 'Deposit settled',
  DEPOSIT_FAILED: 'Deposit failed',
  DEPOSIT_EXPIRED: 'Deposit expired',
  DEPOSIT_RETRY_RECORDED: 'Deposit retry recorded',
  PAYOUT_BREAKER_RESET: 'Payouts restarted',
  PAYOUT_ACCOUNT_REGISTERED: 'Payout account registered',
  PAYOUT_ACCOUNT_REVIEWED: 'Payout account reviewed',
  PAYOUT_ACCOUNT_RETIRED: 'Payout account retired',
  WEBHOOK_INBOX_SEEN: 'StarPay notification marked seen',
};

/* An event this screen has no words for yet is said as words all the same (codeAsWords) —
   "LEDGER_REVERSAL_RAISED" reads "Ledger reversal raised" — so it is never printed as a code. */
export const fundActionWords = (action: string): string =>
  FUND_ACTION_WORDS[action] ?? codeAsWords(action);

/** The fund audit trail's table. It reads nothing but the entry, so it is drawn once, here. */
export const FUND_TRAIL_COLUMNS: Column<AuditEntry>[] = [
  {
    key: 'when',
    header: 'When',
    render: (e) => <span className="tabular">{stamp.format(new Date(e.created_at))}</span>,
  },
  { key: 'actor', header: 'Actor', render: (e) => e.actor_label },
  {
    key: 'action',
    header: 'Action',
    render: (e) => <Tag tone="accent">{fundActionWords(e.action)}</Tag>,
  },
  {
    key: 'reference',
    header: 'Reference',
    render: (e) => (
      <>
        {auditSubjectWords(e.subject_type)}
        {e.subject_id ? <span className="tabular"> · {e.subject_id}</span> : null}
      </>
    ),
  },
];

/* ── The review drawer: its row, read again; its verdict ─────────────────── */

/**
 * ONE REQUEST, READ BY ITS REFERENCE, whatever has become of it.
 *
 * The drawer reads its row again with this after every action taken from it — a release, a
 * refusal, and each action on a parked payout — so the stage, the Payout panel and the verdict
 * buttons follow what the action did. It cannot re-read the list it was opened from: an action
 * moves the row out of it (a release leaves the working queue, a refund leaves the parked list),
 * and the released and refused lists are thousands of rows long. A link to a withdrawal opens one
 * the same way. Null when no request has that reference in the reader's brands.
 */
export async function readWithdrawalByRef(ref: string): Promise<WithdrawalRow | null> {
  const page = await api.get<{ items: WithdrawalRow[]; meta: PageMeta }>(
    `/api/withdrawals?${new URLSearchParams({ ref, perPage: '1' })}`,
  );
  return page.items.find((w) => w.ref === ref) ?? null;
}

type Tell = { toast: (message: string) => void; showError: (detail: ErrorDetail) => void };

/**
 * A VERDICT: given, told, and the row read again.
 *
 * Release moves money and rejection does not, but both are decisions and both follow the same
 * rules: a checker who is not the maker, and a verdict that cannot be given twice. Every refusal
 * the API can return is surfaced to the operator as itself, in the error dialog. A release's own
 * sentence is the server's — it knows whether this rail sends (StarPay) or records, and what StarPay
 * answered — and a release that StarPay refused and refunded, parked or did not answer opens the
 * error dialog with it rather than a toast (`tellOutcome`).
 *
 * `onDecided` is called the moment the verdict has gone through — before the row is read again —
 * so the drawer turns its verdict buttons off then, not when the read lands. The row is read again
 * whether or not the verdict went through: a refusal is often the row having moved under the drawer
 * (a colleague decided it first), and the drawer must show where it stands. `fresh` is null when
 * that read failed or found nothing; the buttons of a verdict that went through stay off regardless.
 */
export async function decideWithdrawal(
  row: WithdrawalRow,
  verdict: 'release' | 'reject',
  body: unknown,
  tell: Tell,
  onDecided: () => void = () => {},
): Promise<{ decided: boolean; fresh: WithdrawalRow | null }> {
  let decided = false;
  try {
    const data = await api.post<{ ref: string; payout: ReleaseOutcome }>(
      `/api/withdrawals/${row.id}/${verdict}`,
      body,
    );
    decided = true;
    onDecided();
    if (verdict === 'release') tellOutcome('RELEASE', row.ref, data.payout, tell);
    else tell.toast(`${row.ref} refused. The player is told the reason.`);
  } catch (error: unknown) {
    tell.showError({
      title: verdict === 'release' ? `Could not release ${row.ref}` : `Could not refuse ${row.ref}`,
      message:
        error instanceof ApiRequestError
          ? error.message
          : 'The decision was not recorded. Try again shortly.',
      ...(error instanceof ApiRequestError ? { code: error.code } : {}),
    });
  }
  let fresh: WithdrawalRow | null = null;
  try {
    fresh = await readWithdrawalByRef(row.ref);
  } catch {
    fresh = null;
  }
  return { decided, fresh };
}

/**
 * Whether a request can still take a verdict here: a role that decides, a request still open
 * (awaiting its checker, or held by risk), and no verdict already given on it from this drawer —
 * `decided` holds from the moment one goes through, before the row is read again. Whether THIS
 * admin may be the one to give it is a separate refusal (`isMine`), said separately.
 */
export const takesVerdict = (row: WithdrawalRow, canApprove: boolean, decided: boolean): boolean =>
  canApprove && !decided && (row.status === 'AWAITING_CHECKER' || row.status === 'HELD_BY_RISK');

/**
 * The drawer's two verdicts. Off the moment the row is decided — `decided` is set as soon as a
 * verdict on this row has gone through, before the row is read again, so neither can be pressed a
 * second time while the read is in flight — and whenever the row cannot take one: a request already
 * released, refused or failed, one this admin raised, a role that cannot decide, or a verdict already
 * on its way.
 */
export function VerdictButtons({
  row,
  canApprove,
  isMine,
  deciding,
  decided,
  gapId,
  onReject,
  onRelease,
}: {
  row: WithdrawalRow;
  canApprove: boolean;
  isMine: boolean;
  /** A verdict on some row is on its way. */
  deciding: boolean;
  /** A verdict on THIS row went through. */
  decided: boolean;
  /** The notice that says why there is no verdict here. */
  gapId: string;
  onReject: () => void;
  onRelease: () => void;
}) {
  const decidable = takesVerdict(row, canApprove, decided);
  const off = !decidable || isMine || deciding;
  return (
    <>
      <Button disabled={off} aria-describedby={decidable ? undefined : gapId} onClick={onReject}>
        Reject request
      </Button>
      <Button
        variant="primary"
        disabled={off}
        aria-describedby={decidable ? undefined : gapId}
        onClick={onRelease}
      >
        {deciding ? 'Releasing…' : 'Release payout'}
      </Button>
    </>
  );
}

/**
 * Who raised a request, as the drawer says it: the admin by name, or the player — a request the
 * player raised carries their own id as its maker, which is how the platform tells the two kinds of
 * maker apart. Null when nobody is on record as raising it. An admin id with no admin row behind it
 * is said the way the adjustment queue says it.
 */
function raisedBy(row: Pick<WithdrawalRow, 'initiatedBy' | 'playerId' | 'makerName'>): string | null {
  if (row.initiatedBy === null) return null;
  if (row.initiatedBy === row.playerId) return 'the player';
  return row.makerName ?? 'an admin no longer on the platform';
}

/**
 * THE MAKER LINE, which is the whole point of the drawer: who raised the request, who decided it,
 * and what that leaves for the admin reading it.
 *
 * It named nobody until the queue carried the names. It said "Their name cannot be shown — this
 * screen is given an internal id with no name attached to it", and of a closed request that the
 * checker's name was "held but not sent to this screen". The queue read now joins both admins'
 * names (`makerName`, `checkerName`), so the line says them.
 *
 * Who may decide is the rule it always was, and its words are unchanged: a request nobody has signed
 * waits for its maker, and the admin who raised one may not decide it. A request decided before the
 * platform kept a checker says that nobody is on record, rather than naming nobody.
 */
export function MakerCheckerLine({
  row,
  sessionId,
  canDecide,
}: {
  row: WithdrawalRow;
  sessionId: string | undefined;
  /** Whether the reader holds funds.approve: only then are they the checker the line offers. */
  canDecide: boolean;
}) {
  const phase = phaseOf(row);
  const raised = raisedBy(row);
  /* A payout that failed after its release was still released, by its checker. Which verb the line
     uses follows the request's status; the stage label is only the word printed before the name. */
  const rejected = row.status === 'REJECTED';
  const decided = PHASE_LABEL[rejected ? 'REJECTED' : 'RELEASED'];
  return (
    <div className="notice">
      <Icon name="shield-check" />
      <div>
        {phase === 'AWAITING_MAKER' ? (
          <>
            {/*
              REWRITTEN TWICE OVER, and both were the same mistake in
              different clothes.

              It said "no route on this platform writes that column
              after the request is created — so there is no first
              signature to give and none can be given here". True when
              written; POST /api/withdrawals/maker shipped afterwards,
              and Sign as maker in the bulk bar on THIS screen is what
              calls it. The panel was telling an operator that the
              button beside it did not exist.

              And it explained the gap by naming a CHECK constraint and
              a column. Neither is something an operator can look up,
              ask for, or repeat to anybody. What they need is what to
              do next.
            */}
            <strong>Nobody has signed this one yet.</strong> It cannot be released or
            refused until somebody has, because that would put one person on both halves
            of a rule that needs two — and this is the one case the platform cannot catch
            for itself, which is why the screen stops it here. Tick it in the queue and
            use <strong>Sign as maker</strong>. Somebody else can release it after that;
            whoever signs cannot.
          </>
        ) : phase === 'RELEASED' || phase === 'REJECTED' || phase === 'FAILED' ? (
          <>
            <strong>Closed.</strong>{' '}
            {raised === null ? 'Nobody is on record as raising it.' : `Raised by ${raised}.`}{' '}
            {row.checkerName === null
              ? `Nobody is on record as ${rejected ? 'rejecting' : 'releasing'} it.`
              : `${decided} by ${row.checkerName}.`}{' '}
            The full trail is there: releasing and rejecting are both recorded together with the
            status change itself, so a request decided on this screen without its record is not a
            state this platform can reach. Open <strong>Fund audit trail</strong> above to read it —
            who, when, and a before-and-after carrying the note they wrote.
          </>
        ) : row.initiatedBy === sessionId ? (
          <>
            {/* WRITTEN FOR THE PERSON READING IT. This said
                "withdrawal_maker_is_not_checker refuses the write at the
                database, not merely in the interface" — the name of a
                CHECK constraint and a permission key, neither of which
                means anything to somebody deciding a payout. The fact
                worth keeping is that it is not this screen being fussy;
                the fact worth dropping is what the rule is called. */}
            <strong>You signed this one first, so you cannot release it.</strong> Someone
            else has to — that is the whole point of two people. This is not the screen
            being careful; nothing can save it either way.
          </>
        ) : raised === null ? (
          /* Held by risk with no maker on record: both verdicts refuse a request with none, so
             there is no checker's part to offer. The player's own route always records a maker;
             this is here so the line never names nobody. */
          <>
            <strong>Nobody is on record as raising it.</strong> It cannot be released or refused
            until a maker is recorded, because one person cannot be both halves of the rule.
          </>
        ) : !canDecide ? (
          /* A reader who may not decide is nobody's checker, so the line only names who raised it. The
             drawer's own notice beside the verdict buttons already says which key a decision needs
             and that this role does not hold it; saying it twice would be noise. */
          <strong>{`Raised by ${raised}.`}</strong>
        ) : (
          <>
            <strong>{`Raised by ${raised}; you would be the checker.`}</strong> It was not you, so
            the maker-checker rule will not block you here.
          </>
        )}
      </div>
    </div>
  );
}

/**
 * THE DRAWER'S ROW, AND WHAT IS KNOWN OF ITS READ — every way they change, in one place.
 *
 *   · `review` is the request the drawer shows, or null while it is closed;
 *   · `read` is the read of a row after an action taken from the drawer, while it is in flight or
 *     when it failed, kept with the request it is about: a late read never marks another request;
 *   · `decidedId` is the request a verdict from this drawer went through on. Its verdict buttons are
 *     off from that moment, before the read lands, if it never does, and whenever it is opened again.
 *
 * OPENING A REQUEST STARTS FROM THE ROW IT WAS OPENED WITH. When a read fails, the drawer says its
 * stage is from before the action and tells the operator to close it and open the request again (so
 * does the console manual) — and the queue is read again after every action. So every opening, from
 * the queue as much as from a link, drops what the last drawer knew of its read; a read still in
 * flight for the request lands on it all the same.
 */
export interface ReviewDrawer {
  review: WithdrawalRow | null;
  read: { id: string; state: 'reading' | 'failed' } | null;
  decidedId: string | null;
}

export type ReviewDrawerEvent =
  /** A request is opened — from the queue, or from a link to it. */
  | { type: 'open'; row: WithdrawalRow }
  | { type: 'close' }
  /** An action on this request was taken from the drawer, and its row is being read again. */
  | { type: 'reading'; id: string }
  /** A verdict on this request went through. */
  | { type: 'decided'; id: string }
  /** The read of this request landed: its row, or null when the read failed or found nothing. */
  | { type: 'read'; id: string; fresh: WithdrawalRow | null };

export const CLOSED_DRAWER: ReviewDrawer = { review: null, read: null, decidedId: null };

/**
 * THE DRAWER FOLLOWS ITS ROW after an action on its parked payout — asking StarPay, confirming it
 * paid, refunding it, re-sending it, closing its hold — whatever the route answered: the row is
 * marked as being read, read again by its reference, and handed to the drawer, or null when the
 * read failed or found nothing. The Payout panel calls this through `onChanged` once an action has
 * settled (`afterPayoutAction`, PayoutPanel.tsx); `followReview` below is that call.
 */
export async function followWithdrawal(
  row: Pick<WithdrawalRow, 'id' | 'ref'>,
  dispatch: (event: ReviewDrawerEvent) => void,
): Promise<void> {
  dispatch({ type: 'reading', id: row.id });
  let fresh: WithdrawalRow | null = null;
  try {
    fresh = await readWithdrawalByRef(row.ref);
  } catch {
    fresh = null;
  }
  dispatch({ type: 'read', id: row.id, fresh });
}

export function reviewDrawer(state: ReviewDrawer, event: ReviewDrawerEvent): ReviewDrawer {
  switch (event.type) {
    case 'open':
      return { ...state, review: event.row, read: null };
    case 'close':
      return { ...state, review: null };
    case 'reading':
      return { ...state, read: { id: event.id, state: 'reading' } };
    case 'decided':
      return { ...state, decidedId: event.id };
    case 'read': {
      /* The drawer takes the row it was given only while it still shows that request: a read that
         lands after the operator closed the drawer, or opened another request, changes nothing on
         screen. */
      const { id, fresh } = event;
      return {
        ...state,
        review: state.review !== null && state.review.id === id && fresh !== null ? fresh : state.review,
        read:
          state.read !== null && state.read.id !== id
            ? state.read
            : fresh === null
              ? { id, state: 'failed' }
              : null,
      };
    }
  }
}

/** The drawer's own read of its row, while it is in flight or when it failed — never another row's. */
export const drawerReading = (drawer: ReviewDrawer): 'reading' | 'failed' | null =>
  drawer.review !== null && drawer.read?.id === drawer.review.id ? drawer.read.state : null;

/* ── Screen ──────────────────────────────────────────────────────────────── */

export function Withdrawals() {
  const { brandId, brands, session, allowed, setBrandId } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  /**
   * Two capabilities this screen READS with, declared up front because state
   * below is initialised from them.
   *
   * `GET /api/platform/settings` is gated on platform.view, NOT on
   * access.manage. The read moved off the write capability so that the roles
   * whose queues these settings govern could read them — see the header of
   * `services/api/src/modules/backoffice/platform.ts`, which names this queue's
   * own thresholds as the reason. Finance Lead, the role this screen is built
   * for, holds platform.view; it does not hold access.manage.
   *
   * Asking for access.manage here was not a stricter check, it was the wrong
   * one: the request was never issued for the role that needed it, the Ratio
   * column was never banded, the payout-ratio check reported `unknown`, and
   * every notice explaining the blank named a capability that has nothing to do
   * with reading a setting.
   */
  const canReadSettings = allowed('platform.view');
  /** The adjustment dialog's player list is `GET /api/players`, gated on players.view. */
  const canReadPlayers = allowed('players.view');

  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab') ?? '';
  const tab: TabKey = isTabKey(rawTab) ? rawTab : 'withdrawals';
  const setTab = useCallback(
    (key: TabKey) => {
      const next = new URLSearchParams(params);
      next.set('tab', key);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  /* Withdrawals tab */
  const [rows, setRows] = useState<WithdrawalRow[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  /** A failed read is not an empty queue, and the table below must not say it is. */
  const [queueError, setQueueError] = useState(false);
  const [page, setPage] = useState(1);
  const [stage, setStage] = useState<StageKey>('QUEUE');
  const [query, setQuery] = useState('');
  /**
   * A WAITING FIGURE THAT ACTUALLY MOVES.
   *
   * `waitingMinutes` is computed by the route and then frozen: this screen had
   * no timer and never read the clock, so the column only changed when the list
   * was re-fetched -- a filter, a page, or a decision. A queue left open for an
   * hour understated every wait by an hour, and "SLA breached" did not appear on
   * a request that crossed the line while an operator was looking at it. That
   * figure is what the SLA judgement is made on.
   *
   * MEASURED AS AN INTERVAL, NOT AS A TIME. The browser clock is only ever used
   * to measure how long since the response arrived, and that drift is added to
   * the server's figure -- so a laptop whose clock is wrong by an hour still
   * shows the right wait. The support desk does the same thing for ticket SLAs
   * and the comment there says why; this is that pattern, one directory over.
   *
   * Thirty seconds, not one: the column is written in minutes, so a faster tick
   * would re-render every row for nothing. The cost is that a minute boundary
   * can be up to 30s late.
   */
  const [now, setNow] = useState(0);
  const [fetchedAt, setFetchedAt] = useState(0);
  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const [sortKey, setSortKey] = useState<SortKey>('waiting');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  /*
   * The review drawer: the request it shows, the read of it after an action, and the request a
   * verdict from it went through on — every change to them is `reviewDrawer`'s. THE DRAWER FOLLOWS
   * ITS ROW: after any action taken from it the row is read again by its reference
   * (`readWithdrawalByRef`), and the stage, the Payout panel and the verdict buttons follow the read.
   * It says its stage may be out of date only about the request it is showing.
   */
  const [drawer, dispatchDrawer] = useReducer(reviewDrawer, CLOSED_DRAWER);
  const { review, decidedId } = drawer;
  const [detail, setDetail] = useState<PlayerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  /* KPI band */
  const [live, setLive] = useState<LivePayload | null>(null);
  /**
   * Whether the live aggregate landed, failed, or is still in flight.
   *
   * `live === null` alone cannot tell those three apart, so both tiles reported
   * the aggregate as unreadable during the first paint of every load — a
   * failure announced before the request had answered. "Not yet" and "not at
   * all" are different facts and the band is only allowed to state the one that
   * happened.
   */
  const [liveState, setLiveState] = useState<ReadState>('loading');

  /**
   * The three figures the band had no source for, off `/api/funds/summary`.
   *
   * A SECOND read beside `/api/dashboard/live` rather than a replacement for
   * it. The two answer different questions — one counts the queue as it stands
   * right now, the other aggregates a gaming day that has already happened —
   * and one failing must not blank the other. Its own ReadState for the same
   * reason the live one has one: "not yet" and "not at all" are different
   * facts, and a tile is only allowed to state the one that happened.
   */
  const [summary, setSummary] = useState<FundsSummary | null>(null);
  const [summaryState, setSummaryState] = useState<ReadState>('loading');

  /**
   * The payout provider's own status, for the Payout provider tile's health line: the connection
   * check made as it is read (`health`), and the driver that answered it (`driverName`). The summary
   * carries the driver and the float but no check. Read on mount and after a restart, not after
   * every decision: each read asks StarPay for its float and its health, and the line says when it
   * checked, so an older reading is never passed off as a new one.
   */
  const [provider, setProvider] = useState<PayoutProviderStatus | null>(null);
  const [providerState, setProviderState] = useState<ReadState>('loading');
  const [providerReads, setProviderReads] = useState(0);

  /**
   * How many payout accounts wait for a second person, for the header.
   *
   * Read on mount, so the count is on the header before the Payout accounts tab is
   * opened — a mismatched name holds a player's payouts, and nobody should have to
   * open a tab to learn there is one. Once the tab is open it keeps this current: it
   * reports the figure each time it reads its waiting list.
   *
   * `null` is "not known", and `reviewReadFailed` says which kind of not-known: a
   * read still in flight has not failed, and a read that failed has not counted
   * nothing — the header says the one that happened, and never a zero for the other.
   */
  const [reviewWaiting, setReviewWaiting] = useState<number | null>(null);
  const [reviewReadFailed, setReviewReadFailed] = useState(false);

  /* The per-brand payout-ratio hold threshold, off platform.setting. */
  const [holdRows, setHoldRows] = useState<PlatformSettingRow[]>([]);
  const [holdState, setHoldState] = useState<ReadState>(
    canReadSettings ? 'loading' : 'forbidden',
  );

  /* Ledger-backed tabs */
  const [deposits, setDeposits] = useState<TransactionRow[]>([]);
  const [depositMeta, setDepositMeta] = useState<PageMeta | null>(null);
  const [depositPage, setDepositPage] = useState(1);
  const [depositFilter, setDepositFilter] = useState<DepositFilterKey>('ALL');
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState(false);
  const [adjustments, setAdjustments] = useState<TransactionRow[]>([]);
  const [adjustmentMeta, setAdjustmentMeta] = useState<PageMeta | null>(null);
  const [adjustmentPage, setAdjustmentPage] = useState(1);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [adjustmentError, setAdjustmentError] = useState(false);
  const [errorCodes, setErrorCodes] = useState<ErrorCodeRow[]>([]);
  /**
   * Whether the provider-code read itself failed, as distinct from a code
   * having no row behind it.
   *
   * Both used to leave the bare code on the row and the same retry sentence
   * under it, so an unread list read exactly like a list with nothing to say —
   * including for a code whose stored guidance is DO NOT RETRY. On a deposit
   * that is guidance an operator acts on, so the row says which of the two
   * happened.
   */
  const [errorCodeState, setErrorCodeState] = useState<ReadState>('loading');

  /* Deposit retry — the record, not the payment. */
  const [retrying, setRetrying] = useState<TransactionRow | null>(null);
  const [retryReason, setRetryReason] = useState<string>(RETRY_REASONS[0]!.code);
  const [retryNote, setRetryNote] = useState('');
  const [recordingRetry, setRecordingRetry] = useState(false);
  /**
   * The attempts already recorded against the deposits on THIS page.
   *
   * Read per page rather than all at once: `/api/deposits/retry-attempts` takes
   * the ids the table has loaded, so the enrichment is bounded by the page and
   * cannot quietly become an unbounded scan of every attempt ever made.
   */
  const [retryAttempts, setRetryAttempts] = useState<DepositRetryAttemptRow[]>([]);
  const [retryAttemptState, setRetryAttemptState] = useState<ReadState>('loading');
  /** Bumped after a recorded attempt so the deposits page re-reads its history. */
  const [retryCount, setRetryCount] = useState(0);

  /* Dialogs */
  const [dialog, setDialog] = useState<'audit' | 'adjust' | null>(null);
  /* Bumped after a decision so the queue re-reads. A decided request must leave
     the queue immediately — an operator working top-down will otherwise open a
     row that no longer needs them. */
  const [decisionCount, setDecisionCount] = useState(0);
  /** Bumped when a StarPay notification is marked seen: the band's count of them is read again. */
  const [bandReads, setBandReads] = useState(0);
  /** The request being refused, and the reason being given for it. */
  const [rejecting, setRejecting] = useState<WithdrawalRow | null>(null);
  const [rejectReason, setRejectReason] = useState<string>(REJECTION_REASONS[0]!.code);
  const [rejectNote, setRejectNote] = useState('');
  /**
   * The optional note that travels with a RELEASE.
   *
   * `releaseWithdrawalRequest` in @dayon/contracts accepts `note` (optional, up
   * to 500 characters) and `POST /api/withdrawals/:id/release` records it in
   * the after-snapshot of the WITHDRAWAL_RELEASED audit row. There is no
   * `release_note` column on `payment.withdrawal` — a release stores its
   * context in the log, which is also where a refusal's note is copied.
   */
  const [releaseNote, setReleaseNote] = useState('');
  const [deciding, setDeciding] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditPayload | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  /**
   * Whether the audit read FAILED, as distinct from a log that holds no fund
   * event.
   *
   * Without it the table below answered a request that never returned with "No
   * fund event has ever been audited — the log holds 0 recent entries": a
   * statement about `platform.audit_log` made without reading it. The operator
   * dismisses the error modal and is left holding a clean bill of health for a
   * trail nobody looked at. "There is nothing to see" and "we could not look"
   * are different facts, and on a fund screen only one of them is safe.
   */
  const [auditFailed, setAuditFailed] = useState(false);
  const [playerOptions, setPlayerOptions] = useState<PlayerRow[]>([]);
  /** `meta.total` of the player query — the scope figure the option list is a page of. */
  const [playerTotal, setPlayerTotal] = useState<number | null>(null);
  const [playerState, setPlayerState] = useState<ReadState>('loading');
  const [playerSearch, setPlayerSearch] = useState('');
  const [adjustPlayer, setAdjustPlayer] = useState('');
  const [adjustDirection, setAdjustDirection] = useState<'CREDIT' | 'DEBIT'>('CREDIT');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState<string>(REASON_CODES[0]!.code);
  const [adjustNote, setAdjustNote] = useState('');
  /** Per-field problems on the raise form. Field-level stays inline. */
  const [adjustFields, setAdjustFields] = useState<Record<string, string>>({});
  const [raising, setRaising] = useState(false);

  /* The checker queue — wallet.manual_adjustment, not the ledger. */
  const [pending, setPending] = useState<AdjustmentRow[]>([]);
  const [pendingMeta, setPendingMeta] = useState<PageMeta | null>(null);
  const [pendingPage, setPendingPage] = useState(1);
  /**
   * Loading / permitted / failed, kept apart for the same reason the queue
   * above keeps them apart: an unread queue is not an empty one, and a table
   * that renders "nothing awaits a checker" over a failed read is telling an
   * operator that money nobody has looked at does not exist.
   */
  const [pendingState, setPendingState] = useState<ReadState>('loading');
  /** Bumped after any adjustment write so the queue and the ledger re-read. */
  const [adjustmentDecisions, setAdjustmentDecisions] = useState(0);
  const [decidingAdjustment, setDecidingAdjustment] = useState<string | null>(null);
  /** The adjustment being refused, and the reason being given for it. */
  const [rejectingAdjustment, setRejectingAdjustment] = useState<AdjustmentRow | null>(null);
  const [adjustRejectReason, setAdjustRejectReason] = useState<string>(
    ADJUSTMENT_REJECTION_REASONS[0]!.code,
  );
  const [adjustRejectNote, setAdjustRejectNote] = useState('');

  /**
   * A brand change re-queries a different — usually smaller — queue. Staying on
   * page 4 of the previous one lands past the end of the new one, on an empty
   * table the operator never asked for (see `pastLastPage`). Adjusted
   * during render rather than in an effect so the queue is fetched once, for
   * page 1, instead of once for the stale page and again for the reset.
   */
  const [pagedBrand, setPagedBrand] = useState(brandId);
  if (pagedBrand !== brandId) {
    setPagedBrand(brandId);
    setPage(1);
    setPendingPage(1);
    setSelected(new Set());
  }

  const canApprove = allowed('funds.approve');
  const canAdjust = allowed('funds.adjust');
  /**
   * The checker queue is a READ, so it is gated on a read capability.
   *
   * `GET /api/adjustments` requires transactions.view — not funds.approve.
   * Gating a read behind a write permission is the mistake that once made
   * payment rails invisible to a Risk Officer; both roles that can decide these
   * rows hold transactions.view as well, so nothing that can act is stopped
   * from looking.
   */
  const canReadLedger = allowed('transactions.view');
  const canAudit = allowed('audit.export');
  /** Only used to choose the open-alerts check's note; without it, the note names the key. */
  const canReadAlerts = allowed('players.restrict');
  const role = session?.role ?? 'unknown';

  /* ── Loads ─────────────────────────────────────────────────────────────── */

  /* Both clocks are zero until the effect above runs; taking the later of the
     two keeps the drift at zero rather than measuring from 1970. */
  const driftMinutes =
    fetchedAt === 0 ? 0 : Math.max(0, Math.floor((Math.max(now, fetchedAt) - fetchedAt) / 60_000));

  /**
   * What this request has waited, now, rather than when the page last read it.
   *
   * `useCallback` because the row columns are MEMOISED. A plain function would
   * be a new value every render, so either the memo recomputes on every render
   * (defeating it) or -- as happened first -- the memo keeps the closure it was
   * built with and the cell never moves at all, while a `setInterval` ticks away
   * behind it doing nothing. Keyed on the drift, so the columns rebuild once a
   * minute and not once a render.
   */
  const waitedFor = useCallback(
    (w: WithdrawalRow): number => w.waitingMinutes + driftMinutes,
    [driftMinutes],
  );

  /**
   * Whether it has missed its target, now.
   *
   * The target is `slaDueAt - requestedAt`, both of which are the SERVER's
   * timestamps, so their difference carries no clock skew -- the same derivation
   * the drawer already uses to print the SLA. The server's own `breachingSla`
   * still counts: it cannot become false while the request sits there.
   */
  const hasBreachedSla = useCallback(
    (w: WithdrawalRow): boolean =>
      w.breachingSla ||
      waitedFor(w) >= (new Date(w.slaDueAt).getTime() - new Date(w.requestedAt).getTime()) / 60_000,
    [waitedFor],
  );

  const stageStatus = STAGE_FILTERS.find((s) => s.key === stage)?.status ?? null;
  /** The payouts waiting for an operator, whatever their status — see STAGE_FILTERS. */
  const stageParked = stage === 'PARKED';

  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadLedger) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setQueueError(false);
    const search = new URLSearchParams({ perPage: String(PAGE_SIZE), page: String(page) });
    if (brandId) search.set('brandId', brandId);
    if (stageStatus) search.set('status', stageStatus);
    if (stageParked) search.set('parked', 'true');

    api
      .get<{ items: WithdrawalRow[]; meta: PageMeta }>(`/api/withdrawals?${search}`)
      .then((d) => {
        if (cancelled) return;
        /* The baseline is when these numbers ARRIVED, not when the request was
           sent and not when the component mounted. */
        setFetchedAt(Date.now());
        setRows(d.items);
        setMeta(d.meta);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Drop the previous page rather than leave it under the new filter, and
        // remember WHY the table is empty: everything downstream — the header
        // count, the empty state, the "awaiting my approval" tile — has to say
        // "the read failed", not "there is nothing".
        setRows([]);
        setMeta(null);
        setQueueError(true);
        showError({
          title: 'Could not load the withdrawal queue',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The payout queue could not be fetched. Try again shortly.',
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canReadLedger, brandId, page, stageStatus, stageParked, showError, decisionCount]);

  useEffect(() => {
    /* The band belongs to the screen, so it waits for the screen's own key: behind the refusal
       below there is no band to fill. A Support Agent read it there anyway, and the PAGCOR
       Regulator — refused this route, which needs players.view — would read a 403. */
    if (!canReadLedger) return;
    let cancelled = false;
    setLiveState('loading');
    const search = new URLSearchParams();
    if (brandId) search.set('brandId', brandId);
    api
      .get<LivePayload>(`/api/dashboard/live?${search}`)
      .then((d) => {
        if (cancelled) return;
        setLive(d);
        setLiveState('ready');
      })
      // The band is a summary of a queue the operator can already see below.
      // A modal over it would interrupt the work list for a decoration. The
      // figures go with the failure all the same: a tile still showing the
      // previous scope's money under a new one is a real number for the wrong
      // population.
      .catch(() => {
        if (cancelled) return;
        setLive(null);
        setLiveState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [canReadLedger, brandId]);

  /**
   * Released today, deposits today, and the checker queue waiting on me.
   *
   * Re-read on `decisionCount` as well as on the brand, because a release
   * changes all three of them at once: it adds to today's released value and
   * payout count, and it takes a request out of the queue this operator is
   * being told about. A band that only refreshed on a brand change would show
   * the operator the workload they had before they did the work.
   *
   * A failure is NOT modal, for the same reason the live read's is not: the
   * band summarises a queue the operator can already see below, and a dialog
   * over a work list for a decoration is an interruption. The tiles say it
   * failed instead — and never carry the previous scope's figures under a new
   * one, which would be a real number for the wrong population.
   */
  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadLedger) {
      setSummaryState('forbidden');
      return;
    }
    let cancelled = false;
    setSummaryState('loading');
    const search = new URLSearchParams();
    if (brandId) search.set('brandId', brandId);
    api
      .get<FundsSummary>(`/api/funds/summary?${search}`)
      .then((d) => {
        if (cancelled) return;
        setSummary(d);
        setSummaryState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setSummary(null);
        setSummaryState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [canReadLedger, brandId, decisionCount, bandReads]);

  /**
   * The count of payout accounts waiting for review: one row's worth of the list, whose
   * `meta.total` is the figure.
   *
   * Brand-scoped like every other figure on this header — a count naming a brand it was
   * not counted in is a real number for the wrong population — and cleared first, so the
   * previous brand's figure is never shown under a new one. A failure is not modal, for
   * the reason the summary's is not: the header summarises a list the operator can open.
   */
  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * would still go out for a role the route refuses.
     */
    if (!canReadLedger) return;
    let cancelled = false;
    setReviewWaiting(null);
    setReviewReadFailed(false);
    const search = new URLSearchParams({ perPage: '1' });
    if (brandId) search.set('brandId', brandId);
    api
      .get<{ meta: PageMeta }>(`/api/payout-accounts/review?${search}`)
      .then((d) => {
        if (!cancelled) setReviewWaiting(d.meta.total);
      })
      .catch(() => {
        if (!cancelled) setReviewReadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [canReadLedger, brandId]);

  /*
   * The provider's status, for the tile. A supporting read like the band's: a failure is said on
   * the tile, never as a modal, and never as "unreachable" — a status that could not be read says
   * nothing about StarPay.
   */
  useEffect(() => {
    if (!canReadLedger) {
      setProviderState('forbidden');
      return;
    }
    let cancelled = false;
    setProviderState('loading');
    api
      .get<PayoutProviderStatus>('/api/payouts/provider')
      .then((d) => {
        if (cancelled) return;
        setProvider(d);
        setProviderState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setProvider(null);
        setProviderState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [canReadLedger, providerReads]);

  /*
   * A LINK TO ONE WITHDRAWAL — `/funds?tab=withdrawals&withdrawal=WDL-…`, which the Parked
   * notifications tab uses — opens its drawer through the same read the drawer re-reads with.
   * A reference that is not in the reader's brands, or not anywhere, says so in the error dialog
   * rather than opening nothing in silence. Closing the drawer takes the reference off the address
   * (`closeReview`), so it is not carried into the next tab or opened again by a reload.
   */
  const linkedRef = params.get('withdrawal');
  useEffect(() => {
    if (!canReadLedger || linkedRef === null) return;
    let cancelled = false;
    readWithdrawalByRef(linkedRef)
      .then((row) => {
        if (cancelled) return;
        if (row !== null) {
          dispatchDrawer({ type: 'open', row });
          return;
        }
        showError({
          title: `Could not open ${linkedRef}`,
          message: `There is no withdrawal ${linkedRef} in the brands you can see.`,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        showError({
          title: `Could not open ${linkedRef}`,
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The withdrawal could not be read. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [canReadLedger, linkedRef, showError]);

  /**
   * The payout-ratio hold threshold, per brand.
   *
   * A supporting read like the KPI band: a failure here must not take the queue
   * down with a modal, so it is recorded as a state the ratio banding reads and
   * the screen says the threshold is unknown. It is NEVER replaced by a
   * hardcoded 0.90 — that is the fabrication this read exists to remove.
   *
   * Not behind the refusal below either: the PAGCOR Regulator holds platform.view
   * and not transactions.view, and would read a threshold for a queue it is
   * never shown.
   */
  useEffect(() => {
    if (!canReadLedger) return;
    if (!canReadSettings) {
      setHoldRows([]);
      setHoldState('forbidden');
      return;
    }
    let cancelled = false;
    setHoldState('loading');
    api
      .get<{ items: PlatformSettingRow[] }>('/api/platform/settings')
      .then((d) => {
        if (cancelled) return;
        setHoldRows(d.items.filter((row) => row.key === PAYOUT_RATIO_HOLD_KEY));
        setHoldState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setHoldRows([]);
        setHoldState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [canReadLedger, canReadSettings]);

  useEffect(() => {
    if (tab !== 'deposits') return;
    /**
     * The MODULE's refusal is a render; this is a hook.
     *
     * The early return further down paints "your role does not hold
     * transactions.view" instead of the tabs — but it runs AFTER every
     * useEffect on this screen has already been registered, because hooks
     * cannot be called conditionally. So the effect still fires, and `tab`
     * comes from the QUERY STRING: `/funds?tab=deposits` was enough to send
     * `GET /api/transactions` for a principal the route refuses, and the 403
     * raised a modal on top of the notice explaining the refusal. One screen
     * saying "not yours to read" and "we tried and it broke" at the same time.
     *
     * The errors effect below has always had this guard. Two of the three reads
     * were written without it.
     */
    if (!canReadLedger) {
      setDeposits([]);
      setDepositMeta(null);
      setDepositLoading(false);
      return;
    }
    let cancelled = false;
    setDepositLoading(true);
    setDepositError(false);
    const search = new URLSearchParams({
      kind: 'DEPOSIT',
      perPage: String(PAGE_SIZE),
      page: String(depositPage),
    });
    if (brandId) search.set('brandId', brandId);
    const depositStatus = DEPOSIT_FILTERS.find((f) => f.key === depositFilter)?.status ?? null;
    if (depositStatus) search.set('status', depositStatus);

    api
      .get<{ items: TransactionRow[]; meta: PageMeta }>(`/api/transactions?${search}`)
      .then((d) => {
        if (cancelled) return;
        setDeposits(d.items);
        setDepositMeta(d.meta);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setDeposits([]);
        setDepositMeta(null);
        setDepositError(true);
        showError({
          title: 'Could not load deposit activity',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The ledger could not be fetched. Try again shortly.',
        });
      })
      .finally(() => {
        if (!cancelled) setDepositLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tab, brandId, depositPage, depositFilter, canReadLedger, showError]);

  /**
   * The retry attempts already recorded against the deposits on this page.
   *
   * Keyed on the LEDGER TRANSACTION, which is what this table lists and what
   * `POST /api/deposits/:txnId/retry-attempts` is addressed by. It has to be:
   * `payment.deposit.transaction_id` is set on settled deposits only, so the
   * FAILED rows the Retry control actually sits on have no payment record
   * behind them at all on this platform's data — a history keyed on the deposit
   * would come back empty for every row that can be retried.
   *
   * Depends on `deposits` so it re-runs when the page's rows change, and on
   * `retryCount` so a newly recorded attempt appears without a reload.
   */
  useEffect(() => {
    if (tab !== 'deposits') return;
    if (!canReadLedger || deposits.length === 0) {
      setRetryAttempts([]);
      setRetryAttemptState(canReadLedger ? 'ready' : 'forbidden');
      return;
    }
    let cancelled = false;
    setRetryAttemptState('loading');
    const search = new URLSearchParams();
    if (brandId) search.set('brandId', brandId);
    // Only the rows that could carry one. A settled deposit has no attempt and
    // asking about it would spend half the id budget saying so.
    for (const t of deposits) {
      if (t.status === 'FAILED') search.append('transactionId', t.id);
    }
    if (!search.has('transactionId')) {
      setRetryAttempts([]);
      setRetryAttemptState('ready');
      return;
    }
    api
      .get<{ items: DepositRetryAttemptRow[] }>(`/api/deposits/retry-attempts?${search}`)
      .then((d) => {
        if (cancelled) return;
        setRetryAttempts(d.items);
        setRetryAttemptState('ready');
      })
      // An enrichment failure interrupts nothing — but the row must not then
      // read as "no attempt has been made", so the state is remembered and the
      // cell says which of the two happened.
      .catch(() => {
        if (cancelled) return;
        setRetryAttempts([]);
        setRetryAttemptState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [tab, brandId, deposits, retryCount, canReadLedger]);

  useEffect(() => {
    if (tab !== 'adjustments') return;
    /**
     * The MODULE's refusal is a render; this is a hook.
     *
     * The early return further down paints "your role does not hold
     * transactions.view" instead of the tabs — but it runs AFTER every
     * useEffect on this screen has already been registered, because hooks
     * cannot be called conditionally. So the effect still fires, and `tab`
     * comes from the QUERY STRING: `/funds?tab=adjustments` was enough to send
     * `GET /api/transactions` for a principal the route refuses, and the 403
     * raised a modal on top of the notice explaining the refusal. One screen
     * saying "not yours to read" and "we tried and it broke" at the same time.
     *
     * The errors effect below has always had this guard. Two of the three reads
     * were written without it.
     */
    if (!canReadLedger) {
      setAdjustments([]);
      setAdjustmentMeta(null);
      setAdjustmentLoading(false);
      return;
    }
    let cancelled = false;
    setAdjustmentLoading(true);
    setAdjustmentError(false);
    const search = new URLSearchParams({
      kind: 'ADJUSTMENT',
      perPage: String(PAGE_SIZE),
      page: String(adjustmentPage),
    });
    if (brandId) search.set('brandId', brandId);

    api
      .get<{ items: TransactionRow[]; meta: PageMeta }>(`/api/transactions?${search}`)
      .then((d) => {
        if (cancelled) return;
        setAdjustments(d.items);
        setAdjustmentMeta(d.meta);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setAdjustments([]);
        setAdjustmentMeta(null);
        setAdjustmentError(true);
        showError({
          title: 'Could not load posted adjustments',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The ledger could not be fetched. Try again shortly.',
        });
      })
      .finally(() => {
        if (!cancelled) setAdjustmentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tab, brandId, adjustmentPage, adjustmentDecisions, canReadLedger, showError]);

  /**
   * The adjustments awaiting a checker.
   *
   * A separate read from the posted ledger below it, because they are separate
   * facts: `wallet.manual_adjustment` holds requests, and a request has no
   * `transaction_id` until it is approved. A pending adjustment therefore
   * cannot appear in `/api/transactions` at all, and the ledger table cannot
   * stand in for the work queue.
   */
  useEffect(() => {
    if (tab !== 'adjustments') return;
    if (!canReadLedger) {
      setPending([]);
      setPendingMeta(null);
      setPendingState('forbidden');
      return;
    }
    let cancelled = false;
    setPendingState('loading');
    const search = new URLSearchParams({
      perPage: String(PAGE_SIZE),
      page: String(pendingPage),
      status: 'AWAITING_CHECKER',
    });
    if (brandId) search.set('brandId', brandId);

    api
      .get<{ items: AdjustmentRow[]; meta: PageMeta }>(`/api/adjustments?${search}`)
      .then((d) => {
        if (cancelled) return;
        setPending(d.items);
        setPendingMeta(d.meta);
        setPendingState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Drop the previous page rather than leave it under a new brand, and
        // remember WHY it is empty — the table below must say "the read
        // failed", never "nothing awaits a checker".
        setPending([]);
        setPendingMeta(null);
        setPendingState('failed');
        showError({
          /*
           * The fallback names WHICH read failed, in the operator's terms.
           *
           * It used to name `wallet.manual_adjustment` — correct, and useless to
           * the person holding the screen, who cannot look that up or quote it
           * to anybody. What they need from this sentence is the one thing the
           * table below cannot tell them on its own: which of the two lists on
           * the adjustments tab went missing. The requests awaiting a verdict
           * and the posted ledger beneath them are separate reads, and the
           * table name was doing that work only by accident.
           */
          title: 'Could not load the adjustment queue',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The queue of credit and debit requests awaiting a checker could not be read. Try again shortly.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tab, brandId, pendingPage, canReadLedger, adjustmentDecisions, showError]);

  // Enrichment only: it turns "MY-504" into the sentence the design prints.
  // A failure here must not take the screen down, so no modal is raised and the
  // raw code stands on its own — but the row says the guidance is UNREAD rather
  // than letting a missing sentence read as a code that carries none.
  //
  // Gated on transactions.view, which is what `GET /api/payments/errors`
  // actually requires (services/api/src/modules/backoffice/catalogue.ts). It
  // was gated on funds.adjust — a WRITE capability, and the wrong one: an
  // Auditor or a Risk Officer reading the deposits tab was shown bare codes and
  // told a permission story about a capability the route never asks for.
  useEffect(() => {
    if (!canReadLedger) {
      setErrorCodes([]);
      setErrorCodeState('forbidden');
      return;
    }
    let cancelled = false;
    setErrorCodeState('loading');
    api
      .get<{ items: ErrorCodeRow[] }>('/api/payments/errors')
      .then((d) => {
        if (cancelled) return;
        setErrorCodes(d.items);
        setErrorCodeState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setErrorCodes([]);
        setErrorCodeState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [canReadLedger]);

  // The player behind the request under review. One extra read, made only when
  // the drawer opens, because the queue endpoint returns no KYC state and no
  // risk score — the two checks the design puts at the top of the list.
  useEffect(() => {
    if (!review) return;
    let cancelled = false;
    setDetailLoading(true);
    /* Read again after an action, too — a release moves the player's balance. The same player's
       record stays on screen while it is read; another player's never does. */
    setDetail((current) => (current?.player.id === review.playerId ? current : null));
    setDetailError(null);
    api
      .get<PlayerDetail>(`/api/players/${review.playerId}`)
      .then((d) => !cancelled && setDetail(d))
      .catch((error: unknown) => {
        if (cancelled) return;
        setDetailError(
          error instanceof ApiRequestError
            ? error.message
            : 'The player record behind this request could not be read.',
        );
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [review]);

  useEffect(() => {
    if (dialog !== 'audit' || !canAudit) return;
    let cancelled = false;
    setAuditLoading(true);
    setAuditFailed(false);
    api
      .get<AuditPayload>('/api/platform/audit')
      .then((d) => {
        if (cancelled) return;
        setAudit(d);
        setAuditFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // The entries go with the failure. A trail read on a previous open,
        // sitting under a notice that says this read failed, is a set of rows
        // presented as current when nobody knows whether they still are.
        setAudit(null);
        setAuditFailed(true);
        showError({
          /*
           * `platform.audit_log` is the table; it is named in the module header
           * above and in `FUND_SUBJECTS` below, which is where a developer
           * needs it. The operator needs to know what the trail IS — the
           * platform's own record of who did what, which is the thing an
           * auditor asks for by that description — and that it is the record,
           * not this dialog's own list, that could not be read.
           */
          title: 'Could not read the audit trail',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The platform’s record of who did what, and when, could not be read. Try again shortly.',
        });
      })
      .finally(() => {
        if (!cancelled) setAuditLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialog, canAudit, showError]);

  /**
   * The adjustment dialog's player options.
   *
   * ONE PAGE of at most `PLAYER_OPTION_PAGE` rows — 200 is the ceiling
   * `pageQuery` enforces — against a roster the seed alone fills with ~2,010
   * players. That page is not the brand scope and the field does not call it
   * one; `meta.total` is kept so the field can say how much of the scope is
   * actually in the list, and the search box narrows it server-side so a player
   * outside the page is still reachable.
   */
  useEffect(() => {
    if (dialog !== 'adjust') return;
    if (!canReadPlayers) {
      setPlayerOptions([]);
      setPlayerTotal(null);
      setPlayerState('forbidden');
      return;
    }
    let cancelled = false;
    setPlayerState('loading');
    const needle = playerSearch.trim();
    // Typing must not fire a request per keystroke; an empty box loads at once.
    const timer = setTimeout(
      () => {
        const search = new URLSearchParams({ perPage: String(PLAYER_OPTION_PAGE) });
        if (brandId) search.set('brandId', brandId);
        if (needle) search.set('q', needle);
        api
          .get<{ items: PlayerRow[]; meta: PageMeta }>(`/api/players?${search}`)
          .then((d) => {
            if (cancelled) return;
            setPlayerOptions(d.items);
            setPlayerTotal(d.meta.total);
            setPlayerState('ready');
            // A selection the new list no longer contains would leave the
            // Select showing a blank while the state still held an id.
            setAdjustPlayer((current) =>
              current && d.items.some((row) => row.id === current) ? current : '',
            );
          })
          .catch(() => {
            if (cancelled) return;
            setPlayerOptions([]);
            setPlayerTotal(null);
            setPlayerState('failed');
          });
      },
      needle ? 300 : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dialog, brandId, playerSearch, canReadPlayers]);

  /* ── Derived ───────────────────────────────────────────────────────────── */

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? rows.filter(
          (w) =>
            w.ref.toLowerCase().includes(needle) ||
            w.playerName.toLowerCase().includes(needle) ||
            w.playerRef.toLowerCase().includes(needle) ||
            w.method.toLowerCase().includes(needle),
        )
      : rows;

    const direction = sortDir === 'asc' ? 1 : -1;
    return matched.toSorted((a, b) => {
      if (sortKey === 'amount') {
        const left = BigInt(a.amount);
        const right = BigInt(b.amount);
        return left === right ? 0 : left < right ? -direction : direction;
      }
      /* WAITING FOR AN OPERATOR IS HOW LONG SINCE THE PARK. On that list a request's own age says
         nothing about how long a person has kept it waiting — a month-old request may have been
         parked an hour ago — so "longest waiting first" is the oldest park first. */
      if (stageParked) return byParkedAt(a, b) * -direction;
      return (a.waitingMinutes - b.waitingMinutes) * direction;
    });
  }, [rows, query, sortKey, sortDir, stageParked]);

  /** Selection is real UI state over real rows; only the action is missing. */
  const selectable = useMemo(
    () => visible.filter((w) => OPEN_PHASES.has(phaseOf(w))),
    [visible],
  );

  /**
   * A SELECTION CANNOT OUTLIVE THE ROWS IT WAS MADE ON.
   *
   * `selected` is a set of ids and survives a reload; `rows` does not. Deciding
   * a request refetches the queue and — unlike the stage chip, the pager, the
   * brand selector and the bulk action, all of which clear it — the verdict path
   * never did. So a ticked row simply left the working queue underneath a live
   * selection, and the bar was left describing a row it no longer had:
   *
   *     1 selected · ₱0.00
   *     Sign 0 as maker
   *     None of the selected rows is waiting for a maker — each already has
   *     one, or has been decided.
   *
   * Three statements, all false. The count came from the raw set while the total
   * came from filtering the loaded page, so a payout screen printed ₱0.00 for a
   * request whose value it simply could not see. The sentence passed a verdict
   * on a row nobody had inspected — it was neither decided nor spoken for, it
   * was NOT LOADED. And the button agreed with the total rather than the count,
   * so it offered to act on nothing.
   *
   * Four expressions in that bar compare against `selected.size`. Rather than
   * teach each one about rows it cannot see, the selection is pruned to what is
   * on the page — after which the count, the total, the button and the sentence
   * all read the same set and are true by construction.
   *
   * THE DROP IS ANNOUNCED. Silently discarding an operator's tick is a smaller
   * lie than the one it replaces, but it is still one: on this screen a tick is
   * how somebody marks a payout they mean to come back to.
   */
  useEffect(() => {
    if (selected.size === 0) return;
    const present = new Set(rows.map((w) => w.id));
    const kept = [...selected].filter((id) => present.has(id));
    if (kept.length === selected.size) return;
    const dropped = selected.size - kept.length;
    setSelected(new Set(kept));
    toast(
      `${formatCount(dropped)} selected request${dropped === 1 ? '' : 's'} ${
        dropped === 1 ? 'is' : 'are'
      } not in this view any more, so ${dropped === 1 ? 'it was' : 'they were'} unselected. A tick ` +
        'this screen cannot see is one it cannot total or act on.',
    );
  }, [rows, selected, toast]);

  const selectedRows = useMemo(() => rows.filter((w) => selected.has(w.id)), [rows, selected]);

  const selectedTotal = useMemo(
    () => selectedRows.reduce((sum, w) => sum + BigInt(w.amount), 0n),
    [selectedRows],
  );

  /**
   * The selected rows that are actually WAITING FOR A MAKER.
   *
   * A selection is made across stages — the checkboxes do not care — so the bulk
   * bar filters before it sends, and says how many it is leaving alone. The
   * route refuses a whole selection carrying a row that cannot move, which is
   * the right rule for a stale list (somebody decided one between this page load
   * and the press) and the wrong one to make an operator fight by hand.
   */
  const takeableIds = useMemo(
    () => selectedRows.filter((w) => phaseOf(w) === 'AWAITING_MAKER').map((w) => w.id),
    [selectedRows],
  );

  /** The confirmation for signing as maker, and the note that rides with it. */
  const [takingAsMaker, setTakingAsMaker] = useState(false);
  const [makerNote, setMakerNote] = useState('');
  const [makerNoteError, setMakerNoteError] = useState<string | undefined>(undefined);
  const [takingMaker, setTakingMaker] = useState(false);

  /**
   * SIGN THE MAKER HALF OF THE SELECTED PAYOUTS.
   *
   * The control reads like an approval and does close to the opposite: by
   * `withdrawal_maker_is_not_checker`, a payout's maker can never release it. So
   * this hands every selected request to somebody else — and the response says
   * how many other admins there are, because an operator alone on duty has just
   * parked all of them and no other screen would tell them.
   */
  const takeAsMaker = useCallback(async () => {
    const trimmed = makerNote.trim();
    if (trimmed.length < 10) {
      setMakerNoteError('Say what you checked — at least 10 characters.');
      return;
    }
    setMakerNoteError(undefined);
    setTakingMaker(true);
    try {
      const result = await api.post<{ taken: number; otherApprovers: number }>(
        '/api/withdrawals/maker',
        { withdrawalIds: takeableIds, note: trimmed },
      );
      toast(
        result.otherApprovers === 0
          ? `${formatCount(result.taken)} signed as maker — and NOBODY ELSE can release them. No other active admin holds Approve withdrawal, so these will wait until one does.`
          : `${formatCount(result.taken)} signed as maker. You can no longer release ${result.taken === 1 ? 'it' : 'them'} — ${formatCount(result.otherApprovers)} other ${result.otherApprovers === 1 ? 'admin' : 'admins'} can.`,
      );
      setTakingAsMaker(false);
      setMakerNote('');
      setSelected(new Set());
      // RE-READ: every row changes stage, and the tiles count the queue by stage.
      setDecisionCount((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields?.['note']) {
        setMakerNoteError(error.fields['note']);
        return;
      }
      showError({
        title: 'Nothing was signed',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The requests were not signed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setTakingMaker(false);
    }
  }, [makerNote, takeableIds, showError, toast]);

  /*
   * "Awaiting my approval" USED TO BE COMPUTED HERE, over `rows` — the loaded
   * page of whichever stage chip was set. It is gone rather than kept beside
   * the real figure: two counts for one tile is how the wrong one ends up on
   * screen. `/api/funds/summary` answers it now, across the whole queue and
   * with the caller's own requests excluded server-side, which is the only
   * place that exclusion can be made against every open row rather than
   * against twenty-five of them.
   */

  /**
   * AFTER AN ACTION ON A PARKED PAYOUT — asking StarPay, confirming it paid, refunding it,
   * re-sending it, closing its hold — the drawer reads its row again (the Payout panel reads its
   * own record), and the queue behind it is read again, so the stage and the buttons follow.
   */
  const followReview = useCallback(async (row: WithdrawalRow) => {
    setDecisionCount((n) => n + 1);
    await followWithdrawal(row, dispatchDrawer);
  }, []);

  /*
   * Release moves money and rejection does not, but both are decisions and both
   * follow the same rules: a checker who is not the maker, and a verdict that
   * cannot be given twice. Every refusal the API can return is surfaced to the
   * operator as itself — a maker-checker conflict reads differently from an
   * overdraw, and both read differently from a permission failure.
   *
   * THE SENTENCE IS THE SERVER'S, told as what it was (`decideWithdrawal`): a toast for a payout
   * that went through, the error dialog for one StarPay refused, parked or did not answer. And the
   * drawer FOLLOWS ITS ROW: the verdict buttons are off the moment a verdict goes through, then the
   * row is read again by its reference — whatever the answer, since a refusal is often a colleague
   * having decided it first — and the queue and its counters are read again behind it.
   */
  const decide = useCallback(
    async (row: WithdrawalRow, verdict: 'release' | 'reject', body: unknown) => {
      setDeciding(row.id);
      dispatchDrawer({ type: 'reading', id: row.id });
      const { decided, fresh } = await decideWithdrawal(row, verdict, body, { toast, showError }, () =>
        dispatchDrawer({ type: 'decided', id: row.id }),
      );
      if (decided) {
        setRejecting(null);
        setRejectNote('');
        setReleaseNote('');
      }
      dispatchDrawer({ type: 'read', id: row.id, fresh });
      setDecisionCount((n) => n + 1);
      setDeciding(null);
    },
    [showError, toast],
  );

  /**
   * Closing the drawer. The release note belongs to the request it was typed against, so it goes
   * with the drawer rather than into the next row; and a link's reference leaves the address, so a
   * reload or the next tab does not open this request again.
   */
  const closeReview = useCallback(() => {
    dispatchDrawer({ type: 'close' });
    setReleaseNote('');
    if (params.has('withdrawal')) {
      const next = new URLSearchParams(params);
      next.delete('withdrawal');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  /**
   * Raise an adjustment. NO money moves here.
   *
   * `POST /api/adjustments` writes a row to `wallet.manual_adjustment` with
   * status AWAITING_CHECKER and this admin as the maker. The balance is
   * untouched until a second admin approves it, and the toast says exactly
   * that rather than implying the player has been paid.
   *
   * Validation happens twice on purpose. The inline checks below are so the
   * operator is told which field is wrong before a round trip; the contract on
   * the server is what actually decides, and its per-field messages replace
   * these if the two ever disagree.
   */
  const raiseAdjustment = useCallback(async () => {
    const fields: Record<string, string> = {};
    if (!adjustPlayer) fields['playerId'] = 'Choose the player this adjustment is against.';

    let centavos = 0n;
    try {
      centavos = parsePesos(adjustAmount);
      if (centavos <= 0n) {
        fields['amount'] = 'An adjustment must be for more than zero. Direction carries the sign.';
      }
    } catch {
      fields['amount'] = 'Enter a peso amount with at most two decimals, e.g. 1,234.56.';
    }

    /* A debit that would take the account below zero is refused here as well as
       by the route. The figure is the balance this screen last read, so the
       server re-checks it under a lock — this only saves the operator a round
       trip on the obvious case. */
    const holder = playerOptions.find((row) => row.id === adjustPlayer);
    if (
      !fields['amount'] &&
      adjustDirection === 'DEBIT' &&
      holder &&
      BigInt(holder.cashBalance) < centavos
    ) {
      fields['amount'] =
        `${holder.name} holds ${formatPesos(BigInt(holder.cashBalance))}. A debit cannot take an account below zero.`;
    }

    if (adjustNote.trim().length < 10) {
      fields['note'] = 'Say what happened — at least 10 characters. A second admin reads this.';
    }

    setAdjustFields(fields);
    if (Object.keys(fields).length > 0) return;

    setRaising(true);
    try {
      const made = await api.post<{ ref: string }>('/api/adjustments', {
        playerId: adjustPlayer,
        direction: adjustDirection,
        amount: centavos.toString(),
        reasonCode: adjustReason,
        note: adjustNote.trim(),
      });
      toast(
        `${made.ref} raised for ${formatPesos(centavos)} — awaiting a second admin. No money has moved.`,
      );
      setDialog(null);
      setAdjustAmount('');
      setAdjustNote('');
      setAdjustPlayer('');
      setAdjustFields({});
      // Re-read rather than push the row in: the request belongs in the checker
      // queue, and the tab's counts move with it.
      setAdjustmentDecisions((n) => n + 1);
    } catch (error: unknown) {
      // Field-level problems stay inline; anything else opens the modal.
      if (error instanceof ApiRequestError && error.fields) {
        setAdjustFields(error.fields);
        return;
      }
      showError({
        title: 'Could not raise the adjustment',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The request was not recorded. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setRaising(false);
    }
  }, [
    adjustPlayer,
    adjustDirection,
    adjustAmount,
    adjustReason,
    adjustNote,
    playerOptions,
    showError,
    toast,
  ]);

  /**
   * Approve or refuse one, as the checker.
   *
   * Approving is the only place on this tab where money moves: the route posts
   * the balanced pair of ledger entries, links the transaction to the request
   * and writes the audit row in one transaction. Refusing moves nothing —
   * raising never took anything, so refusing returns nothing.
   */
  const decideAdjustment = useCallback(
    async (row: AdjustmentRow, verdict: 'approve' | 'reject', body: unknown) => {
      setDecidingAdjustment(row.id);
      try {
        await api.post(`/api/adjustments/${row.id}/${verdict}`, body);
        toast(
          verdict === 'approve'
            ? `${row.ref} approved — ${formatPesos(BigInt(row.amount))} ${
                row.direction === 'CREDIT' ? 'credited to' : 'debited from'
              } ${row.playerName}.`
            : `${row.ref} refused. The maker is told the reason.`,
        );
        setRejectingAdjustment(null);
        setAdjustRejectNote('');
        // Re-read both tables: the row leaves the queue and, on an approval,
        // appears in the posted ledger below with a brand-new transaction.
        setAdjustmentDecisions((n) => n + 1);
      } catch (error: unknown) {
        showError({
          title:
            verdict === 'approve' ? `Could not approve ${row.ref}` : `Could not refuse ${row.ref}`,
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The decision was not recorded. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      } finally {
        setDecidingAdjustment(null);
      }
    },
    [showError, toast],
  );

  /**
   * Record a deposit retry attempt. NOTHING is re-submitted to a provider.
   *
   * The toast says both halves, because a success message on a control labelled
   * "Retry" reads as "the deposit was re-sent" and it was not. The route says
   * the same thing in its own payload (`providerSubmission.attempted: false`)
   * and `deposit_retry_not_submitted` on the table refuses a row that claims
   * otherwise, so the three cannot drift apart.
   *
   * Every refusal reaches the operator as itself: the cooldown names the
   * attempt that already exists and who made it, which is something an operator
   * can act on, and a permission failure reads as a permission failure.
   */
  const recordRetry = useCallback(
    async (row: TransactionRow, reasonCode: string, note: string) => {
      setRecordingRetry(true);
      try {
        const made = await api.post<{ ref: string; attemptCount: number }>(
          `/api/deposits/${row.id}/retry-attempts`,
          { reasonCode, note },
        );
        toast(
          `${made.ref} recorded against ${row.ref} — attempt ${formatCount(made.attemptCount)}. ` +
            'Nothing was sent to the provider and no money moved.',
        );
        setRetrying(null);
        setRetryNote('');
        setRetryCount((n) => n + 1);
      } catch (error: unknown) {
        showError({
          title: `Could not record a retry attempt on ${row.ref}`,
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The attempt was not recorded. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      } finally {
        setRecordingRetry(false);
      }
    },
    [showError, toast],
  );

  const toggleRow = useCallback((id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /**
   * Sort, in one pair of updates.
   *
   * This used to call setSortDir from INSIDE the setSortKey updater. Updaters
   * must be pure and StrictMode double-invokes them, so the direction toggle was
   * enqueued twice and netted to no change: the arrow never flipped and the rows
   * never reversed. Both decisions are made out here from the current state
   * instead, and each updater does one pure thing.
   */
  const toggleSort = useCallback(
    (key: SortKey) => {
      if (key === sortKey) {
        setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
        return;
      }
      setSortKey(key);
      setSortDir('desc');
    },
    [sortKey],
  );

  /**
   * The design's empty-state button. It resets the GLOBAL brand scope too,
   * which is the design's own definition — that scope is the only filter the
   * deposits and adjustments tabs actually have, so a reset that left it in
   * place would leave those two empty states unchanged and look broken.
   */
  const resetFilters = useCallback(() => {
    setQuery('');
    setStage('QUEUE');
    setPage(1);
    setDepositPage(1);
    setDepositFilter('ALL');
    setAdjustmentPage(1);
    setSelected(new Set());
    setBrandId(null);
  }, [setBrandId]);

  /** The design appends the arrow to the header label of the active column. */
  const arrow = useCallback(
    (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''),
    [sortKey, sortDir],
  );

  /**
   * The payout-ratio hold threshold FOR ONE BRAND, or null when it is not known.
   *
   * A brand override wins over the global row, which is exactly how
   * `platform.setting` scopes a `per_brand` key. Null means "no threshold to
   * band against" — never a fallback value.
   */
  const holdFor = useCallback(
    (brandName: string): HoldThreshold | null => {
      if (holdState !== 'ready') return null;
      const row = holdRows.find((s) => s.brand === brandName) ?? holdRows.find((s) => s.brand === null);
      if (!row) return null;
      const bp = ratioToBp(row.value);
      return bp === null ? null : { bp, scope: row.brand === null ? 'global' : 'brand' };
    },
    [holdRows, holdState],
  );

  /** Why a ratio is or is not banded, said in the operator's terms. */
  const holdNote = useCallback(
    (brandName: string): string => {
      const hold = holdFor(brandName);
      if (hold) {
        /*
         * "Holds", not "configures". This threshold is EVALUATED, and the code
         * that evaluates it is `services/api/src/modules/player-money/
         * withdrawals.ts`: the route that raises a withdrawal reads this exact
         * key for the player's brand, divides lifetime RELEASED withdrawals by
         * lifetime SETTLED deposits, and when the ratio is above the threshold
         * writes the row as HELD_BY_RISK with `held_reason` = "Payout ratio
         * above N" — the same sentence the seed's held row carries. An absent
         * or unreadable setting means NO hold, never a default one.
         *
         * Two things about it an operator has to be told, because both change
         * what the number beside this note means:
         *
         *   · the comparison happens ONCE, at the instant the request is
         *     raised, whereas the Ratio column is recomputed on every read of
         *     this queue. A row can sit above the line here and never have been
         *     held, because it was under the line when it was queued.
         *   · the hold deliberately does NOT carry the risk-alert prefix that
         *     `risk.reconcile_critical_holds` recognises (the route says so in
         *     the comment beside it: a hold placed for a reason the reconciler
         *     knows nothing about is a hold it must not lift). So this one is
         *     never lifted by the worker. A checker on this screen releases or
         *     refuses it, or it stays.
         */
        return `${brandName} holds a payout above ${formatRatio(hold.bp)}${
          hold.scope === 'brand' ? ', a limit this brand sets for itself' : ', the limit used across every brand'
        }. The check runs ONCE, the moment the player asks — the figure above is worked out fresh every time you open this screen, so a payout can sit above the line here and never have been held, because it was under the line when it was asked for. Whether this one was held is the row below, not this number. Nothing lifts such a hold on its own: you release it or you refuse it.`;
      }
      if (holdState === 'loading') {
        return 'Reading the limit this brand holds payouts above; until it arrives the figure has no line to sit against.';
      }
      /*
       * THE THREE WAYS THERE IS NO LINE, AND WHY THEY ARE NOT ONE SENTENCE.
       *
       * Forbidden, failed, and genuinely-not-set each leave the Ratio column
       * unbanded and mean completely different things to somebody deciding a
       * payout: a number nobody here is allowed to see, a number nobody managed
       * to fetch, and no number existing at all. Only the last of those three
       * says anything about how this brand is configured, and collapsing them
       * would let an operator read "no limit is set" off a request that merely
       * timed out.
       *
       * What left these sentences is the IDENTIFIERS, not the distinction. The
       * setting is `platform.setting`, the key is the
       * `PAYOUT_RATIO_HOLD_KEY` above (`withdrawal.payout_ratio_hold`, with
       * `per_brand = true`), and the read is `GET /api/platform/settings` —
       * all three documented in the comment on that constant, which is where a
       * developer looks and where an operator never does. `platform.view`
       * STAYS: a capability is the one kind of name on this screen that an
       * operator can look up on the permission matrix and ask somebody for.
       */
      if (holdState === 'forbidden') {
        return `The limit each brand holds payouts above is kept in the platform settings, and every brand may set its own. Reading it needs the platform.view capability, which ${role} does not hold, so this figure is shown with no line drawn against it rather than compared with a number invented by this screen. The limit still applies the moment a player asks for a payout — this role simply cannot read the number it is applied against.`;
      }
      if (holdState === 'failed') {
        return `The platform settings could not be read, so the limit this brand holds payouts above is unknown and this figure is shown with no line drawn against it. That is a read that failed, not a brand without a limit.`;
      }
      return `No payout limit is recorded for ${brandName}, and none is set across all brands either. The settings were read successfully, so there is genuinely nothing configured here — not a read that failed — and this figure therefore has no line to sit against.`;
    },
    [holdFor, holdState, role],
  );

  const withdrawalColumns = useMemo<Column<WithdrawalRow>[]>(
    () => [
      {
        key: 'select',
        header: 'Select',
        render: (w) => {
          const open = OPEN_PHASES.has(phaseOf(w));
          return (
            <Choice
              checked={selected.has(w.id)}
              disabled={!open}
              onChange={() => toggleRow(w.id)}
              label={<span className="sr-only">Select request {w.ref}</span>}
            />
          );
        },
      },
      {
        key: 'ref',
        header: 'Request',
        render: (w) => (
          <>
            <span className="tabular">{w.ref}</span>
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {w.playerRef}
            </div>
          </>
        ),
      },
      { key: 'player', header: 'Player', render: (w) => w.playerName },
      {
        key: 'brand',
        header: 'Brand',
        render: (w) => <span className="text-muted">{w.brandName}</span>,
      },
      {
        key: 'rail',
        header: 'Rail',
        render: (w) => (
          <>
            <span className="text-muted">{w.method}</span>
            <div className="text-subtle tabular" style={{ fontSize: '11px' }}>
              {w.railAccountMask ?? 'no mask stored'}
            </div>
          </>
        ),
      },
      {
        /* Where the money stands once it is released — a separate question from
           the Stage beside it. A dash until a verdict has released it; the
           parked reason under the state when the platform stopped acting on it. */
        key: 'payout',
        header: 'Payout',
        render: (w) => {
          const l = payoutLabel(w);
          return l ? (
            <>
              <Tag tone={payoutTone(w.payoutState)}>{l}</Tag>
              {w.parkedReason && (
                <div className="text-subtle" style={{ fontSize: '11px' }}>
                  {PARKED_REASON_WORDS[w.parkedReason]}
                </div>
              )}
            </>
          ) : (
            <span className="text-subtle">—</span>
          );
        },
      },
      {
        key: 'amount',
        header: `Amount${arrow('amount')}`,
        numeric: true,
        render: (w) => <span className="tabular">{formatPesos(BigInt(w.amount))}</span>,
      },
      {
        key: 'ratio',
        header: 'Ratio',
        numeric: true,
        // Recomputed by the endpoint on every read from CURRENT lifetime totals
        // (see RATIO_PROVENANCE) — the stored payout_ratio_bp column is never
        // returned and never written. Banded only against the brand's own
        // configured threshold, and left unbanded when that is not readable.
        render: (w) => {
          if (w.payoutRatioBp === null) {
            return <span className="text-subtle">no settled deposits</span>;
          }
          const hold = holdFor(w.brandName);
          const over = hold !== null && w.payoutRatioBp > hold.bp;
          return (
            <>
              <span className={over ? 'tabular stat-delta up' : 'tabular'}>
                {formatRatio(w.payoutRatioBp)}
              </span>
              {over && hold ? (
                <div className="text-subtle" style={{ fontSize: '11px' }}>
                  {/* Above the configured line — which is not the same as held.
                      The threshold IS evaluated, but once, at the moment the
                      request was raised, and this ratio is recomputed on every
                      read; the Status column is the only statement about what
                      actually happened to the row. */}
                  above {formatRatio(hold.bp)} threshold
                </div>
              ) : null}
            </>
          );
        },
      },
      {
        key: 'risk',
        header: 'Risk',
        // held_reason is the only risk signal /api/withdrawals returns. The
        // design's banded score is player.risk_score, which the queue SELECT
        // does not include — it is read in the drawer instead, not guessed here.
        render: (w) =>
          w.heldReason ? (
            <Tag tone="outline">{holdWords(w.heldReason)}</Tag>
          ) : (
            <span className="text-subtle">no hold</span>
          ),
      },
      {
        key: 'waiting',
        header: `Waiting${arrow('waiting')}`,
        numeric: true,
        render: (w) => (
          <>
            <span className={hasBreachedSla(w) || waitedFor(w) > 40 ? 'tabular stat-delta up' : 'tabular'}>
              {formatWait(waitedFor(w))}
            </span>
            {hasBreachedSla(w) && (
              <div className="text-subtle" style={{ fontSize: '11px' }}>
                SLA breached
              </div>
            )}
          </>
        ),
      },
      {
        key: 'stage',
        header: 'Stage',
        render: (w) => <StageTag row={w} />,
      },
      {
        key: 'action',
        header: 'Action',
        render: (w) => {
          const phase = phaseOf(w);
          const label =
            phase === 'AWAITING_MAKER' ? 'Review' : OPEN_PHASES.has(phase) ? 'Check' : 'View';
          return (
            <Button
              small
              variant="primary"
              onClick={() => dispatchDrawer({ type: 'open', row: w })}
              aria-label={`${label} ${w.ref}`}
            >
              {label}
            </Button>
          );
        },
      },
    ],
    // `arrow` is memoised on sortKey/sortDir, so depending on it is depending
    // on both — the header labels re-render when the sort flips. `holdFor`
    // carries the settings read, so the banding re-renders when it lands.
    // `waitedFor` and `hasBreachedSla` carry the drift, and WITHOUT THEM the
    // waiting column is frozen at whatever it read on mount — the timer runs,
    // the value updates, and this memo hands back the old closure regardless.
    [selected, arrow, toggleRow, holdFor, waitedFor, hasBreachedSla],
  );

  const errorMeaning = useCallback(
    (code: string | null): ErrorCodeRow | null =>
      code === null ? null : (errorCodes.find((e) => e.code === code) ?? null),
    [errorCodes],
  );

  /**
   * Attempts already recorded, indexed by the ledger row they were made
   * against — newest first, which is the order the endpoint answers in.
   */
  const retryHistory = useMemo(() => {
    const byTransaction = new Map<string, DepositRetryAttemptRow[]>();
    for (const a of retryAttempts) {
      const list = byTransaction.get(a.transactionId);
      if (list) list.push(a);
      else byTransaction.set(a.transactionId, [a]);
    }
    return byTransaction;
  }, [retryAttempts]);

  const depositColumns = useMemo<Column<TransactionRow>[]>(
    () => [
      {
        key: 'ref',
        header: 'Ledger ref',
        render: (t) => (
          <>
            <span className="tabular">{t.ref}</span>
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {t.brandName ?? 'no brand'}
            </div>
          </>
        ),
      },
      {
        key: 'player',
        header: 'Player',
        render: (t) => (
          <>
            {t.playerName ?? 'no player'}
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {t.playerRef ?? '—'}
            </div>
          </>
        ),
      },
      {
        key: 'rail',
        header: 'Rail',
        secondary: true,
        // wallet.ledger_transaction has no method_id. The rail is on
        // payment.deposit, which no endpoint returns.
        render: () => <span className="text-subtle">not on the ledger</span>,
      },
      {
        key: 'amount',
        header: 'Amount',
        numeric: true,
        render: (t) => <span className="tabular">{formatPesos(BigInt(t.amount), { sign: true })}</span>,
      },
      {
        key: 'status',
        header: 'Status',
        render: (t) => {
          const meaning = errorMeaning(t.providerErrorCode);
          return (
            <>
              <Tag tone={t.status === 'SETTLED' ? 'neutral' : 'outline'}>
                {TXN_STATUS_LABEL[t.status]}
              </Tag>
              {t.providerErrorCode && (
                <div className="text-subtle" style={{ fontSize: '11px' }}>
                  {meaning
                    ? `${meaning.meaning} (${t.providerErrorCode})`
                    : errorCodeState === 'failed'
                      ? `${t.providerErrorCode} · meaning unread, the provider code list could not be fetched`
                      : t.providerErrorCode}
                </div>
              )}
            </>
          );
        },
      },
      {
        key: 'time',
        header: 'Time',
        numeric: true,
        render: (t) => (
          <>
            <span className="tabular">{clock.format(new Date(t.occurredAt))}</span>
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {stamp.format(new Date(t.occurredAt))}
            </div>
          </>
        ),
      },
      {
        key: 'action',
        header: 'Action',
        // Only a FAILED row gets a Retry, as the design specifies. A REVERSED
        // one has already been undone in the ledger; retrying it is not a thing.
        // `POST /api/deposits/:id/retry-attempts` refuses anything but FAILED
        // as well, so the rule holds when the call does not come from here.
        render: (t) => {
          if (t.status !== 'FAILED') return null;
          const meaning = errorMeaning(t.providerErrorCode);
          const attempts = retryHistory.get(t.id) ?? [];
          const latest = attempts[0];
          return (
            <>
              <Button
                small
                disabled={!canApprove || recordingRetry}
                aria-describedby={`retry-gap-${t.id}`}
                onClick={() => {
                  setRetrying(t);
                  setRetryNote('');
                  setRetryReason(RETRY_REASONS[0]!.code);
                }}
              >
                Retry
              </Button>
              <div id={`retry-gap-${t.id}`} className="text-subtle" style={{ fontSize: '11px' }}>
                {/* THE SPLIT, on every row that offers the control: the attempt
                    is written down, and the money does not move. A button
                    labelled Retry that quietly meant "log a note" would be the
                    same lie as one that quietly meant nothing at all. */}
                {canApprove
                  ? 'Records an attempt — no provider call, no money moves'
                  : `Recording an attempt is not something ${role} can do`}
                {/* A code whose stored guidance is DO NOT RETRY and a code
                    whose guidance was never read must not print the same
                    sentence — the second one is silence, not clearance. */}
                {meaning ? (
                  meaning.retry_safe ? null : (
                    <div>
                      <strong>Provider says do not retry.</strong>
                    </div>
                  )
                ) : errorCodeState === 'failed' ? (
                  <div>
                    Whether this code is retry-safe is unknown — the provider&rsquo;s error-code
                    list could not be read.
                  </div>
                ) : null}
                {retryAttemptState === 'failed' ? (
                  <div>Attempt history unread — that read failed; it is not "none recorded".</div>
                ) : latest ? (
                  <div>
                    {formatCount(attempts.length)} recorded · last {latest.ref} by{' '}
                    {latest.requestedByLabel ?? 'an admin with no name on file'} at{' '}
                    {clock.format(new Date(latest.createdAt))}
                  </div>
                ) : null}
              </div>
            </>
          );
        },
      },
    ],
    [errorMeaning, errorCodeState, retryHistory, retryAttemptState, canApprove, recordingRetry, role],
  );

  const adjustmentColumns = useMemo<Column<TransactionRow>[]>(
    () => [
      {
        key: 'ref',
        header: 'Adjustment',
        render: (t) => (
          <>
            <span className="tabular">{t.ref}</span>
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {stamp.format(new Date(t.occurredAt))}
            </div>
          </>
        ),
      },
      {
        key: 'player',
        header: 'Player',
        render: (t) => (
          <>
            {t.playerName ?? 'no player'}
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {t.playerRef ?? '—'}
            </div>
          </>
        ),
      },
      {
        key: 'direction',
        header: 'Direction',
        // Read off the SIGN of the player-side ledger entries, which is what a
        // credit and a debit actually are. `manual_adjustment.direction` is the
        // stored column and no endpoint returns it.
        render: (t) => (
          <Tag tone={BigInt(t.amount) >= 0n ? 'neutral' : 'outline'}>
            {BigInt(t.amount) >= 0n ? 'Credit' : 'Debit'}
          </Tag>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        numeric: true,
        render: (t) => <span className="tabular">{formatPesos(BigInt(t.amount), { sign: true })}</span>,
      },
      {
        key: 'reason',
        header: 'Reason',
        secondary: true,
        render: () => <span className="text-subtle">not on the ledger</span>,
      },
      {
        key: 'maker',
        header: 'Maker',
        secondary: true,
        render: () => <span className="text-subtle">not on the ledger</span>,
      },
      {
        key: 'stage',
        header: 'Stage',
        render: (t) => (
          <Tag tone={t.status === 'SETTLED' ? 'neutral' : 'outline'}>
            {TXN_STATUS_LABEL[t.status]}
          </Tag>
        ),
      },
    ],
    [],
  );

  /**
   * The checker queue itself — `wallet.manual_adjustment`, awaiting a verdict.
   *
   * Everything the posted-ledger table above has to say "not on the ledger" to
   * is on this row: the stored direction, the reason code, the maker, and the
   * note they wrote. A pending adjustment has no ledger transaction, which is
   * exactly why it needs its own table rather than a column on that one.
   */
  const pendingColumns = useMemo<Column<AdjustmentRow>[]>(
    () => [
      {
        key: 'ref',
        header: 'Adjustment',
        render: (a) => (
          <>
            <span className="tabular">{a.ref}</span>
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {stamp.format(new Date(a.createdAt))}
            </div>
          </>
        ),
      },
      {
        key: 'player',
        header: 'Player',
        render: (a) => (
          <>
            {a.playerName}
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {a.playerRef} · holds {formatPesos(BigInt(a.playerCashBalance))}
            </div>
          </>
        ),
      },
      {
        key: 'direction',
        header: 'Direction',
        // The STORED direction, off manual_adjustment.direction — not inferred
        // from an entry sign, because nothing has been posted yet.
        render: (a) => (
          <Tag tone={a.direction === 'CREDIT' ? 'neutral' : 'outline'}>
            {a.direction === 'CREDIT' ? 'Credit' : 'Debit'}
          </Tag>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        numeric: true,
        render: (a) => (
          <span className="tabular">
            {formatPesos(a.direction === 'CREDIT' ? BigInt(a.amount) : -BigInt(a.amount), {
              sign: true,
            })}
          </span>
        ),
      },
      {
        key: 'reason',
        header: 'Reason',
        render: (a) => (
          <>
            {REASON_LABEL[a.reasonCode] ?? a.reasonCode}
            {a.reasonNote ? (
              <div className="text-subtle" style={{ fontSize: '11px' }}>
                {a.reasonNote}
              </div>
            ) : (
              <div className="text-subtle" style={{ fontSize: '11px' }}>
                raised before a note was required — no note was recorded
              </div>
            )}
          </>
        ),
      },
      {
        key: 'maker',
        header: 'Maker',
        secondary: true,
        render: (a) => (
          <>
            {a.makerLabel ?? 'an admin no longer on the platform'}
            {session && a.initiatedBy === session.id ? (
              <div className="text-subtle" style={{ fontSize: '11px' }}>
                you
              </div>
            ) : null}
          </>
        ),
      },
      {
        key: 'checker',
        header: 'Checker action',
        render: (a) => {
          if (!canApprove) {
            return (
              <span className="text-subtle">
                Deciding needs funds.approve — {role} does not hold it
              </span>
            );
          }
          /* The maker may not be the checker. The route refuses it and
             `manual_adjustment_maker_is_not_checker` refuses it again at the
             database, so the control is disabled with the rule beside it rather
             than offered and then rejected. */
          const mine = session !== null && a.initiatedBy === session.id;
          if (mine) {
            return (
              <span className="text-subtle">You raised this — a second admin must decide it</span>
            );
          }
          const busy = decidingAdjustment !== null;
          return (
            <span className="row">
              <Button
                small
                disabled={busy}
                aria-label={`Refuse ${a.ref}`}
                onClick={() => {
                  setRejectingAdjustment(a);
                  setAdjustRejectReason(ADJUSTMENT_REJECTION_REASONS[0]!.code);
                  setAdjustRejectNote('');
                }}
              >
                Reject
              </Button>
              <Button
                small
                variant="primary"
                disabled={busy}
                aria-label={`Approve ${a.ref}`}
                onClick={() => void decideAdjustment(a, 'approve', {})}
              >
                {decidingAdjustment === a.id ? 'Posting…' : 'Approve'}
              </Button>
            </span>
          );
        },
      },
    ],
    [canApprove, role, session, decidingAdjustment, decideAdjustment],
  );

  /* ── Drawer facts ──────────────────────────────────────────────────────── */

  /*
   * Whether this request can still take a decision, and whether THIS admin may
   * be the one to give it.
   *
   * Kept apart because they refuse for different reasons and the operator needs
   * to be told which: a decided request is finished, whereas one they raised
   * themselves is waiting for somebody else. A verdict that went through from
   * this drawer decides it at once (`decidedId`), before the row is read again.
   */
  const reviewDecidable =
    review !== null && takesVerdict(review, canApprove, decidedId === review.id);
  const reviewIsMine =
    review !== null && review.initiatedBy !== null && review.initiatedBy === session?.id;
  /** The drawer's own read of its row, while it is in flight or when it failed — never another row's. */
  const reviewReading = drawerReading(drawer);
  const reviewPlayer = detail?.player ?? null;
  /** This request's brand threshold, or null when the setting is not readable. */
  const reviewHold = review ? holdFor(review.brandName) : null;

  /*
   * The duplicate count is now the ROUTE's, not this page's.
   *
   * It used to be counted over the rows loaded into the table, and said so — a
   * player with a second request on page three read as having none. The queue
   * endpoint now counts across the whole queue in the same SELECT, so the figure
   * is the queue's answer rather than the paging's.
   */

  /** The SLA window, read off this row rather than asserted: sla_due_at minus
      requested_at is the interval the platform actually applied to it. */
  const slaMinutes = review
    ? Math.round(
        (new Date(review.slaDueAt).getTime() - new Date(review.requestedAt).getTime()) / 60_000,
      )
    : 0;

  const checks: CheckRow[] = review
    ? [
        {
          key: 'kyc',
          label: 'KYC status',
          state: reviewPlayer
            ? reviewPlayer.kyc_status === 'VERIFIED'
              ? 'ok'
              : 'attention'
            : 'unknown',
          value: reviewPlayer ? (
            (KYC_LABEL[reviewPlayer.kyc_status] ?? reviewPlayer.kyc_status)
          ) : detailLoading ? (
            'reading the player record…'
          ) : (
            <span className="text-subtle">unavailable</span>
          ),
          ...(reviewPlayer && reviewPlayer.kyc_status !== 'VERIFIED'
            ? { note: 'Ok only when Verified.' }
            : {}),
        },
        /* A real check now: the holder's name was compared with the player's
           verified ID when the account was registered (payout-state.ts). */
        { key: 'nameMatch', label: 'Account holder’s name', ...nameMatchCheck(review) },
        {
          key: 'ratio',
          label: 'Payout ratio',
          // Pass or fail needs BOTH a ratio and the brand's configured
          // threshold. With either missing the check is unknown — it does not
          // quietly fall back to a threshold this file made up.
          state:
            review.payoutRatioBp === null || reviewHold === null
              ? 'unknown'
              : review.payoutRatioBp > reviewHold.bp
                ? 'attention'
                : 'ok',
          value:
            review.payoutRatioBp === null ? (
              <span className="text-subtle">no settled deposits to divide by</span>
            ) : (
              <span className="tabular">{formatRatio(review.payoutRatioBp)}</span>
            ),
          note: `${RATIO_PROVENANCE} ${holdNote(review.brandName)}`,
        },
        {
          key: 'alerts',
          label: 'Open risk alerts',
          /* CRITICAL and HIGH raise the flag; MEDIUM and LOW are counted and
             left quiet. A flag on every severity is a flag operators learn to
             click past, and this screen already carries four other checks
             competing for the same attention. */
          state:
            review.openAlerts === 0
              ? 'ok'
              : review.worstAlertSeverity === 'CRITICAL' || review.worstAlertSeverity === 'HIGH'
                ? 'attention'
                : 'unknown',
          value:
            review.openAlerts === 0 ? (
              'None open'
            ) : (
              <span className="row">
                <span className="tabular">
                  {formatCount(review.openAlerts)} open
                </span>
                {review.worstAlertSeverity === null ? (
                  <span className="text-subtle">severity not recognised</span>
                ) : (
                  <Tag tone={review.worstAlertSeverity === 'CRITICAL' ? 'outline' : 'neutral'}>
                    worst: {SEVERITY_LABEL[review.worstAlertSeverity]}
                  </Tag>
                )}
              </span>
            ),
          note:
            review.openAlerts === 0
              ? 'Alerts still open against this player. A real zero — one that has already been closed is not counted, because somebody looked at it and answered it.'
              : canReadAlerts
                ? 'How many and how serious, not what they say — open the Risk module to read which rule fired. It does not stop the release on its own; an open alert is a reason to look, and the hold below is what actually stops one.'
                : /* The key and who to ask: permission keys stay on screen. */
                  alertsNoteWithoutKey(role),
        },
        {
          key: 'hold',
          label: 'Risk hold on this request',
          state: review.heldReason ? 'attention' : 'ok',
          value: review.heldReason ? holdWords(review.heldReason) : 'None',
          note: 'The reason recorded against this request when the player asked for it.',
        },
        {
          key: 'wagering',
          label: 'Wagering complete',
          /* `attention` means LOOK, not REFUSE. No rule on this platform
             forfeits a bonus when a player withdraws, so an outstanding
             requirement is a fact the checker weighs — the same standing as the
             payout ratio, and unlike KYC it blocks nothing by itself. */
          state: review.openBonusGrants === 0 ? 'ok' : 'attention',
          value:
            review.openBonusGrants === 0 ? (
              'No bonus running'
            ) : (
              <span className="tabular">
                {formatPesos(BigInt(review.wageringOutstanding))} still to wager across{' '}
                {formatCount(review.openBonusGrants)}{' '}
                {review.openBonusGrants === 1 ? 'bonus' : 'bonuses'}
              </span>
            ),
          note:
            review.openBonusGrants === 0
              ? 'Bonuses still running for this player. A real zero — every row in the queue is counted, not just this one.'
              : 'This does NOT block the payout: nothing on this platform forfeits a bonus when a player withdraws, and releasing leaves the grant running to its own expiry. It is here because a checker should know before deciding.',
        },
        {
          key: 'duplicate',
          label: 'Duplicate request',
          /* The same figure at the same moment is what reads as a double
             submission; two different figures is usually a player splitting a
             payout, which is not a fault. So the two are counted separately and
             only the first one raises the flag. */
          state: review.otherOpenSameAmount > 0 ? 'attention' : 'ok',
          value:
            review.otherOpenRequests === 0 ? (
              'No other request in the queue'
            ) : (
              <>
                {formatCount(review.otherOpenRequests)} other open request
                {review.otherOpenRequests === 1 ? '' : 's'} for this player
                {review.otherOpenSameAmount > 0 ? (
                  <>
                    , <strong>{formatCount(review.otherOpenSameAmount)} for the same amount</strong>
                  </>
                ) : (
                  ', none for the same amount'
                )}
              </>
            ),
          note:
            review.otherOpenSameAmount > 0
              ? 'Two requests for the identical figure is what a double submission looks like. Deciding one does not touch the other — each is released or refused on its own, so refuse the duplicate rather than leaving it in the queue.'
              : 'Counted across the WHOLE queue, not the page loaded here. A payout already released is not counted: this is about requests still waiting, not the player’s history.',
        },
      ]
    : [];

  /* ── Gate ──────────────────────────────────────────────────────────────── */

  /*
   * READING THE QUEUE IS A READ, and is gated as one.
   *
   * This used to refuse the whole module to anyone without `funds.approve`,
   * explaining that `GET /api/withdrawals` needed it too. The route has since
   * moved to `transactions.view` — a payout queue is something a Risk Officer
   * and an Auditor have every reason to read and neither can decide — so the
   * old gate was locking two roles out of a screen the API would now serve
   * them, with a sentence that had stopped being true.
   *
   * The DECISION controls remain gated on `funds.approve` individually, which
   * is where that capability belongs: a reader sees the queue and no verdict
   * buttons, rather than a lock icon and an explanation of a 403 that would
   * not happen.
   */
  if (!canReadLedger) {
    return (
      <EmptyState icon="lock-key" title="Player funds">
        This module reads the payout queue, the deposit ledger and the adjustment history, and all
        three need the <code>transactions.view</code> capability. Your role, {role}, does not hold
        it. Deciding a request needs <code>funds.approve</code> on top of it, which is a separate
        key held by fewer roles.
      </EmptyState>
    );
  }

  const brandLabel = brandId ? (brands.find((b) => b.id === brandId)?.name ?? brandId) : 'All brands';

  /**
   * "in this view" means THE TAB ON SCREEN.
   *
   * Each tab is a different dataset with its own meta, and the header used to
   * print the withdrawal query's total over all three — a real number from the
   * wrong population, captioning a deposits or adjustments table. Every tab now
   * answers with its own count, and says which of the three things it is when it
   * has none: still loading, unreadable because the read failed, or a real zero.
   *
   * The Payout accounts tab has none in this line, deliberately: its list is a
   * different population again, and its own count is the waiting clause below, the
   * figure a person opens it to bring to zero. It is still given a branch of its own
   * — a final `else` here would caption it with the adjustments figure.
   */
  /**
   * A page past the end of the withdrawal queue.
   *
   * `/api/withdrawals` takes its window count from `count(*) OVER ()`, which
   * only exists on returned rows — but it runs that count through `windowTotal`
   * (services/api/src/list-query.ts), and that helper RE-PROBES the same
   * filtered query for a single row when the page came back empty. So
   * `meta.total` past the end is the real size of the queue, not a 0 invented by
   * the page number, and this screen reports it rather than refusing to. (It
   * used to say the total was unreadable here, which was true of an earlier
   * handler and is not true of this one.)
   *
   * What IS still true is that the rows are gone, so the page number has to be
   * explained: an empty table under "5 requests in this view" only reads
   * honestly if the screen says which page it is on and where the last one is.
   */
  const pastLastPage = !loading && !queueError && rows.length === 0 && page > 1;

  const view =
    tab === 'withdrawals'
      ? { meta, failed: queueError, noun: 'requests' }
      : tab === 'deposits'
        ? { meta: depositMeta, failed: depositError, noun: 'deposit transactions' }
        : tab === 'adjustments'
          ? {
              meta: adjustmentMeta,
              failed: adjustmentError,
              /*
               * This tab carries TWO tables — the checker queue above the posted
               * ledger — and the header used to count only the second. It read
               * "0 posted adjustments" directly above two requests awaiting a
               * verdict, which is the same defect as a total that describes a
               * different dataset than the rows beneath it.
               */
              noun:
                pendingMeta && pendingMeta.total > 0
                  ? `posted adjustments, with ${formatCount(pendingMeta.total)} awaiting a checker above them,`
                  : 'posted adjustments',
            }
          : null;

  /**
   * What the two live tiles say in place of a figure.
   *
   * Never a zero and never a bare "unavailable": a read still in flight has not
   * failed, and a read that failed has not counted nothing.
   */
  const liveGap =
    liveState === 'failed'
      ? 'This could not be read, so nothing was counted — the blank is not a zero'
      : 'reading the live aggregate…';

  /** The same distinction for the gaming-day aggregate, which is its own read. */
  const summaryGap =
    summaryState === 'failed'
      ? 'This could not be read, so nothing was added up — the blank is not a zero'
      : 'reading today’s aggregate…';

  /**
   * Which day "today" is, spelled out.
   *
   * The boundary is Asia/Manila midnight, which is 16:00 UTC — so for eight
   * hours of every UTC day the gaming day and the calendar day an operator's
   * laptop shows are different days. The band prints the instant the day began
   * rather than leaving that to be assumed.
   */
  const gamingDayNote = summary
    ? `${summary.gamingDay.timezone} gaming day, from ${stamp.format(new Date(summary.gamingDay.from))}`
    : '';

  /** How the Ratio column is banded right now, in one clause. */
  const ratioBandingNote =
    holdState === 'loading'
      ? 'that read is still in flight, so no row is banded yet.'
      : holdState === 'forbidden'
        ? `reading that limit needs the platform.view capability, which ${role} does not hold, so no row is banded and no limit is asserted anywhere on this screen — though the limit itself still applies when a request is raised.`
        : holdState === 'failed'
          ? 'that read failed, so no row is banded — which is not the same as every brand allowing every ratio.'
          : holdRows.length === 0
            /*
             * The same three-way distinction as `holdNote`, one clause long,
             * and the same identifiers left out of it: this branch is
             * `platform.setting` holding no `withdrawal.payout_ratio_hold` row
             * for any brand and no global one either. It is the SUCCESSFUL
             * read of that setting, which is why it says so — the two branches
             * above are the unreadable ones, and a table under this note with
             * nothing banded has to be distinguishable from a table under
             * those.
             */
            ? 'no payout limit is recorded for any brand, nor one across all of them, so nothing here is banded — the settings were read, and there is genuinely nothing set.'
            : 'each row is compared with its own brand value — a limit the brand sets for itself where there is one, the limit used across every brand otherwise.';

  const viewCount =
    view === null
      ? null
      : tab === 'withdrawals' && pastLastPage && meta
        ? `page ${formatCount(page)} is past the end · ${formatCount(meta.total)} ${view.noun} in this view`
        : view.meta
          ? `${formatCount(view.meta.total)} ${view.noun} in this view`
          : view.failed
            ? `${view.noun} count unavailable — that list could not be read`
            : `counting ${view.noun}…`;

  /**
   * The header's last clause, whatever the tab: the payout accounts waiting for a second
   * person. FOUR-state like `viewCount`, and for the same reason — "no payout accounts
   * waiting" is said only when the read succeeded and returned zero. Derived from the
   * read, never written down, and in the same brand scope as the rest of the line.
   */
  const reviewCaption =
    reviewWaiting !== null
      ? reviewWaiting === 0
        ? ' · no payout accounts waiting for review'
        : ` · ${formatCount(reviewWaiting)} payout account${reviewWaiting === 1 ? '' : 's'} waiting for review`
      : reviewReadFailed
        ? ' · count of payout accounts waiting for review unavailable — that list could not be read'
        : ' · counting payout accounts waiting for review…';

  const chosenPlayer = playerOptions.find((p) => p.id === adjustPlayer) ?? null;

  /**
   * What the option list actually is.
   *
   * At most one page of PLAYER_OPTION_PAGE rows — the ceiling the contract
   * allows — against a roster the seed alone fills with ~2,010 players. So the
   * field reports it as "the first N of TOTAL", using the query's own
   * `meta.total`, and never as the brand scope. The three ways the list can be
   * short of that are kept apart: still reading, not permitted, read failed.
   *
   * Two of these branches used to end "from /api/players", naming the read they
   * describe — `GET /api/players`, issued by the effect above. An operator
   * cannot open that address, cannot quote it to anybody who will act on it, and
   * it tells them nothing that "read live" does not tell them better, so the
   * path is recorded here for whoever reads the source and the sentence keeps
   * the claim it was making.
   */
  /* The scope inside the sentence, in plain lower-case words: the chosen brand, the one brand of
     an admin held to one, or "all 3 brands" — not the header's capitalised "All brands"
     (fix round 1, M7). */
  const hintScope = brandId ? brandLabel : allBrandsPhrase(brands);
  const playerScopeWord = playerSearch.trim()
    ? `matching players in ${hintScope}`
    : `players in ${hintScope}`;

  function describePlayerOptions(): string {
    if (playerState === 'loading') return 'Looking up the player…';
    if (playerState === 'forbidden') {
      return `${role} cannot read the player list, so there is nothing here to choose from.`;
    }
    if (playerState === 'failed') {
      return 'The player list could not be read. That is a read that failed, not a brand with no players in it.';
    }
    if (playerTotal === null) return `${formatCount(playerOptions.length)} players loaded, read live.`;
    if (playerTotal === 0) {
      return `The read succeeded and returned no ${playerScopeWord} — a real zero, not a list that failed to load.`;
    }
    if (playerTotal > playerOptions.length) {
      return `The first ${formatCount(playerOptions.length)} of ${formatCount(playerTotal)} ${playerScopeWord}, ordered by last seen — ${formatCount(PLAYER_OPTION_PAGE)} is the per-page maximum, so the other ${formatCount(playerTotal - playerOptions.length)} are not in this list. Search above to reach them.`;
    }
    /* One is "the one", never "All 1 matching players" (module walk, 30 Oct 2026). */
    if (playerTotal === 1)
      return `The one ${playerSearch.trim() ? 'matching ' : ''}player in ${hintScope}, read live.`;
    return `All ${formatCount(playerTotal)} ${playerScopeWord}, read live.`;
  }

  const playerHint = describePlayerOptions();

  const fundAuditRows = (audit?.entries ?? []).filter((e) => FUND_SUBJECTS.has(e.subject_type));
  /*
   * WHOSE ENTRIES THE TRAIL READ. For a reader whose access covers every brand the API reads the
   * newest entries of the whole log; for one restricted to some brands it reads only the entries
   * recorded against those brands — none recorded against no brand — and names them in
   * `restrictedTo`, since it is what applied the restriction. Every sentence in the dialog about
   * which entries were read takes its words from this: "the whole log", said to a restricted
   * reader, describes entries they were never given. Until the API has answered, or when it could
   * not be read, the session's own restriction stands in. Null means the whole log.
   */
  const sessionRestriction =
    session && session.brands.length > 0 ? session.brands.map((b) => b.name) : null;
  const trailBrands = audit ? (audit.restrictedTo ?? null) : sessionRestriction;

  return (
    <>
      <ScreenHeader
        title="Player funds"
        purpose="Where the finance team decides money leaving and entering a player's wallet, and a second person signs off on whatever somebody else raised."
        /*
          The caption kept exactly as it was, all three parts of it.

          "Maker-checker enforced" is a standing fact about the screen and not a
          derived one, but it has always been the first thing in this line and
          the figures after it read as a consequence of it — a count of requests
          means something different once you know none of them can be decided by
          the person who raised them. It stays where it was rather than being
          promoted into `purpose`, which is prose about the screen's job; this is
          the state of the screen right now.

          `viewCount` is the part that must not be touched. It is computed just
          above and it is FOUR-state, deliberately: a page past the end of the
          queue, a real count, "that list could not be read" when the fetch
          failed, and "counting…" while it is still in flight. The third of
          those is the one that earns the whole line — an empty table under a
          count that silently said zero would have an operator believing a brand
          has no requests when nobody successfully asked the question. Each tab
          supplies its own count and noun through `view`, because the header used
          to print the withdrawal total over all three tabs.
        */
        meta={
          <>
            Maker-checker enforced · {brandLabel}
            {viewCount === null ? null : ` · ${viewCount}`}
            {reviewCaption}
          </>
        }
        actions={
          <>
            <Button onClick={() => setDialog('audit')} disabled={!canAudit}>
              <Icon name="receipt" />
              Fund audit trail
            </Button>
            <Button variant="primary" onClick={() => setDialog('adjust')} disabled={!canAdjust}>
              <Icon name="plus" />
              New adjustment
            </Button>
          </>
        }
      />

      {/* The rule the whole screen exists to serve, stated where the queue is,
          not in a tooltip. */}
      <div className="notice notice-quiet">
        <Icon name="shield-check" />
        <div>
          <strong>Maker-checker.</strong> Approving records you as the maker; a second admin
          holding <code>funds.approve</code> releases it, and the same person can never do both.
          That is not an honour system — the platform&rsquo;s own records refuse to store a payout
          whose maker and checker are the same person, and refuse a rejection with no reason. Even a mistake in
          our own code could not get one past it. The queue is worked oldest-first.
        </div>
      </div>

      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          <strong>Both money surfaces are live. A deposit retry records an attempt and nothing
          else.</strong>{' '}
          Open a request and release or refuse it: releasing moves the money in the ledger, stamps
          you as the checker and records the change together — all of it lands, or none of it does.
          Refusing records your reason. Manual adjustments work the same way in two steps: raising
          one moves nothing, and approving it is what actually posts the money. Every one of them
          refuses a maker deciding their own request, and neither a release nor a debit will take an
          account below zero. Deposit retry is deliberately half a feature, and the missing half is
          said wherever the control appears: it records who tried, why, and against which deposit,
          and it does not re-send anything to the payment provider.{' '}
          <em>
            Releasing a payout on a StarPay rail posts the ledger movement and hands the transfer to
            StarPay in the same action; the row then reads Sent to StarPay, With the bank, Paid, or
            Failed and refunded. On every other rail releasing records the payout and a person
            sends it.
          </em>
        </div>
      </div>

      {/* ── KPI band ───────────────────────────────────────────────────────── */}
      <div className="stat-grid">
        <Stat
          label="Pending withdrawals"
          value={
            live ? formatPesosCompact(BigInt(live.live.withdrawalsPending)) : '—'
          }
          delta={
            live
              ? `${formatCount(live.live.withdrawalCountPending)} requests · oldest ${
                  live.live.oldestPendingMinutes === null
                    ? 'n/a'
                    : formatWait(live.live.oldestPendingMinutes)
                }${
                  live.queue.withdrawalsAwaitingChecker.breachingSla > 0
                    ? ` · ${live.queue.withdrawalsAwaitingChecker.breachingSla} past SLA`
                    : ''
                }`
              : liveGap
          }
          deltaUp={(live?.queue.withdrawalsAwaitingChecker.breachingSla ?? 0) > 0}
        />

        {/* The design's second tile is "Released today · 318 payouts · avg 22m",
            and all three figures are real now: `GET /api/funds/summary`
            aggregates `payment.withdrawal.released_at` over the ASIA/MANILA
            gaming day — the boundary `gamingDay()` draws for every writer on
            this platform, not a UTC midnight this screen invented. Releasing a
            payout re-reads it, so the tile counts the operator's own work. */}
        <Stat
          label="Released today"
          value={summary ? formatPesosCompact(BigInt(summary.releasedToday.value)) : '—'}
          delta={
            summary
              ? `${formatCount(summary.releasedToday.payoutCount)} payout${
                  summary.releasedToday.payoutCount === 1 ? '' : 's'
                } · ${
                  summary.releasedToday.averageReleaseMinutes === null
                    ? 'no payout to average'
                    : `avg ${formatWait(summary.releasedToday.averageReleaseMinutes)} request to release`
                } · ${gamingDayNote}`
              : summaryGap
          }
        />

        {/* The label promised a day and the figure covered an hour. Both now
            say the same thing: the gaming day. `/api/dashboard/live`'s
            60-minute window still exists and is still right — it is just not
            what this tile was captioned as, so it is not what this tile
            reads. The failure rate is counted on `created_at`, because a
            failed deposit never settles and a rate keyed on settlement could
            only ever be zero. */}
        <Stat
          label="Deposits · today"
          value={summary ? formatPesosCompact(BigInt(summary.depositsToday.settledValue)) : '—'}
          delta={
            summary
              ? `${formatCount(summary.depositsToday.settledCount)} settled · ${formatCount(
                  summary.depositsToday.failedCount,
                )} failed · ${
                  summary.depositsToday.failureRateBp === null
                    ? 'no deposit attempted, so there is no failure rate'
                    : `${formatRatePercent(summary.depositsToday.failureRateBp)} failure rate`
                } · ${gamingDayNote}`
              : summaryGap
          }
          deltaUp={(summary?.depositsToday.failedCount ?? 0) > 0}
        />

        <Stat
          label="Awaiting my approval"
          value={
            summary && summary.awaitingMyApproval.count !== null
              ? formatCount(summary.awaitingMyApproval.count)
              : '—'
          }
          delta={
            /*
             * This tile sits ABOVE the tabs and reads like a global figure, and
             * now it is one. It used to be computed over `rows` — the loaded
             * page of whichever stage chip was set below — so moving the chip
             * to Released dropped it to 0, which was true of that view and
             * false of the queue. `/api/funds/summary` counts the WHOLE open
             * queue in this brand scope and subtracts the requests this
             * operator raised, which is the maker-checker rule the database
             * enforces with `withdrawal_maker_is_not_checker`. It no longer
             * follows the chip, and no longer claims to.
             */
            summary
              ? summary.awaitingMyApproval.count === null
                ? `as checker · ${role} · ${summary.awaitingMyApproval.unavailable}`
                : `as checker · ${role} · of ${formatCount(
                    summary.awaitingMyApproval.openTotal,
                  )} open in ${brandLabel}, less the ${formatCount(
                    summary.awaitingMyApproval.raisedByMe,
                  )} you raised · the whole queue, not this page`
              : summaryGap
          }
        />

        {/* The payout provider, off the same summary read: which driver answers,
            the float StarPay last reported, and the three counts of what is with
            it or waiting on a person. Under the MOCK driver the float is the
            mock's own figure, not StarPay's, so the tile says "Mock driver"
            where the float would be. The inbox count is a NUMBER only — an inbox
            row's own error text can name the schema, and no screen prints it —
            and it says where the notifications are listed: this screen's
            Parked notifications tab, which the tile opens, read with the same
            brand choice so its waiting rows are this count. The line under it
            is the provider's connection check (`healthLine`), from its own read. */}
        <Stat
          label="Payout provider"
          value={summary ? providerFigure(summary.payouts) : '—'}
          delta={
            summary
              ? `${formatCount(summary.payouts.inFlight)} in flight · ${formatCount(
                  summary.payouts.parked,
                )} waiting for an operator · ${formatCount(summary.payouts.inboxParked)} StarPay notification${
                  summary.payouts.inboxParked === 1 ? '' : 's'
                } waiting for a person — listed under Parked notifications on this screen`
              : summaryGap
          }
          deltaUp={(summary?.payouts.parked ?? 0) > 0 || (summary?.payouts.inboxParked ?? 0) > 0}
        >
          <div className="text-subtle" style={{ fontSize: '11.5px' }}>
            {providerState === 'ready' && provider
              ? healthLine(provider)
              : providerState === 'failed'
                ? 'The provider’s status could not be read, so nothing here says whether StarPay is reachable.'
                : providerState === 'loading'
                  ? 'checking the provider…'
                  : null}
          </div>
          {tab === 'parked-notifications' ? null : (
            <button
              type="button"
              className="link-button"
              style={{ fontSize: '11.5px', marginTop: 'var(--space-1)' }}
              onClick={() => setTab('parked-notifications')}
            >
              Open Parked notifications
            </button>
          )}
        </Stat>
      </div>

      {/* THE STOP, said where the band is, and the one way to lift it (PayoutStop.tsx). A restart
          reads the band again, which is where the stop is shown, and the provider's status, whose
          connection check the tile reports. */}
      <PayoutStop
        tripped={summary?.payouts.breakerTripped ?? false}
        canRestart={canApprove}
        role={role}
        onRestarted={() => {
          setDecisionCount((n) => n + 1);
          setProviderReads((n) => n + 1);
        }}
      />

      <Tabs label="Player funds sections" active={tab} onChange={setTab} tabs={TABS} />

      {/* ── Withdrawals ────────────────────────────────────────────────────── */}
      {tab === 'withdrawals' && (
        <Panel>
          <div className="row">
            <div className="search-field" style={{ flex: '1 1 260px', maxWidth: '420px' }}>
              <Icon name="magnifying-glass" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Request ID, player, player ID or rail"
                aria-label="Search by request ID, player, player ID or rail"
              />
            </div>
            {/* Sort is a pair of real buttons rather than a clickable table
                header: the shared DataTable takes a plain string header, and a
                header you cannot reach from the keyboard is not a control. The
                arrow the design shows is appended to the header label above. */}
            <Button
              small
              aria-pressed={sortKey === 'waiting'}
              onClick={() => toggleSort('waiting')}
            >
              Waiting{arrow('waiting')}
            </Button>
            <Button small aria-pressed={sortKey === 'amount'} onClick={() => toggleSort('amount')}>
              Amount{arrow('amount')}
            </Button>
          </div>

          <div className="row" style={{ marginTop: 'var(--space-3)' }}>
            <Chips
              label="Filter withdrawals by stage"
              active={stage}
              onChange={(key) => {
                setStage(key);
                setPage(1);
                setSelected(new Set());
              }}
              options={STAGE_FILTERS.map((s) => ({ key: s.key, label: s.label }))}
            />
          </div>

          {/* The bulk bar. The action reads like an approval and is closer to
              the opposite — see `takeAsMaker` — so the label says what it does
              and the count beside it is the rows it can actually act on. */}
          {selected.size > 0 && (
            <div className="notice" style={{ marginTop: 'var(--space-3)' }}>
              <Icon name="coins" />
              <div className="row">
                <span>
                  <strong>{formatCount(selected.size)}</strong> selected ·{' '}
                  <span className="tabular">{formatPesos(selectedTotal)}</span>
                </span>
                {canApprove ? (
                  <Button
                    small
                    variant="primary"
                    className="header-spacer"
                    disabled={takeableIds.length === 0 || takingMaker}
                    aria-describedby={takeableIds.length === 0 ? 'bulk-gap' : undefined}
                    onClick={() => setTakingAsMaker(true)}
                  >
                    {takeableIds.length === selected.size
                      ? `Sign as maker (${formatCount(takeableIds.length)})`
                      : `Sign ${formatCount(takeableIds.length)} as maker`}
                  </Button>
                ) : (
                  <span
                    id="bulk-gap"
                    className="text-subtle header-spacer"
                    style={{ fontSize: '11.5px' }}
                  >
                    Signing as maker needs{' '}
                    <span className="signin-perm">funds.approve</span>, which {role} does not hold.
                  </span>
                )}
                <Button small variant="ghost" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
                {canApprove && takeableIds.length < selected.size ? (
                  <span id="bulk-gap" className="text-subtle" style={{ fontSize: '11.5px' }}>
                    {takeableIds.length === 0
                      ? 'None of the selected rows is waiting for a maker — each already has one, or has been decided.'
                      : `${formatCount(selected.size - takeableIds.length)} of these already has a maker or has been decided, and will be left alone.`}
                  </span>
                ) : null}
              </div>
            </div>
          )}

          <div className="row" style={{ marginTop: 'var(--space-3)' }}>
            <Choice
              checked={selectable.length > 0 && selectable.every((w) => selected.has(w.id))}
              disabled={selectable.length === 0}
              onChange={(e) =>
                setSelected(e.target.checked ? new Set(selectable.map((w) => w.id)) : new Set())
              }
              label={`Select all open requests shown (${formatCount(selectable.length)})`}
            />
          </div>

          {loading && rows.length === 0 ? (
            <div className="skeleton" style={{ height: '240px', marginTop: 'var(--space-3)' }}>
              &nbsp;
            </div>
          ) : (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <DataTable
                caption="Withdrawal requests with player, brand, payout rail, where the payout stands, amount, payout ratio, risk hold, waiting time and maker-checker stage"
                columns={withdrawalColumns}
                rows={visible}
                rowKey={(w) => w.id}
                empty={
                  queueError ? (
                    <EmptyState icon="warning-circle" title="The withdrawal queue could not be read">
                      The read failed, so this table is empty because we could not fetch it — not
                      because the queue is. The reason is in the message that just opened. Nothing
                      on this screen is a count of anything right now.
                    </EmptyState>
                  ) : pastLastPage ? (
                    <EmptyState
                      icon="wallet"
                      title={`Page ${formatCount(page)} is past the end of this queue`}
                      action={
                        <Button
                          onClick={() => {
                            setPage(1);
                            setSelected(new Set());
                          }}
                        >
                          Back to page 1
                        </Button>
                      }
                    >
                      The count beside the heading is still real — it is re-checked against the
                      same filters whenever a page comes back empty. There{' '}
                      {meta && meta.total === 1 ? 'is' : 'are'} {meta ? formatCount(meta.total) : 'the'}{' '}
                      request
                      {meta && meta.total === 1 ? '' : 's'} here
                      {meta
                        ? `, which is ${formatCount(Math.max(1, Math.ceil(meta.total / PAGE_SIZE)))} page${
                            Math.max(1, Math.ceil(meta.total / PAGE_SIZE)) === 1 ? '' : 's'
                          } at ${formatCount(PAGE_SIZE)} a page.`
                        : '.'}{' '}
                      What is missing here is rows, not the count — this page number is past the
                      last one.
                    </EmptyState>
                  ) : (
                    <EmptyState
                      icon="wallet"
                      title={`No withdrawal requests for ${brandLabel}`}
                      action={<Button onClick={resetFilters}>Reset filters</Button>}
                    >
                      Nothing matches the brand you have selected and the filters you have set.
                      The read succeeded, so this is a real zero and not a screen that failed to
                      load.
                    </EmptyState>
                  )
                }
              />
            </div>
          )}

          <div className="text-subtle" style={{ fontSize: '12px', paddingTop: 'var(--space-2)' }}>
            {pastLastPage && meta
              ? `page ${formatCount(page)} is past the end of the queue · ${formatCount(meta.total)} requests in it`
              : meta
                ? `${formatCount(visible.length)} of ${formatCount(meta.total)} requests`
                : queueError
                  ? 'the queue read failed — there is no count to report'
                  : 'counting requests…'}
            {query ? ' · search filters the loaded page only' : ''}
          </div>

          {meta && (
            <Pagination
              page={meta.page}
              perPage={meta.perPage}
              total={meta.total}
              onPage={(next) => {
                setPage(next);
                setSelected(new Set());
              }}
            />
          )}

          <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="info" />
            <div>
              <strong>What this table is, exactly.</strong> The queue arrives oldest-first, a page
              at a time. <strong>The search box and the two sort buttons only reorder the page you
              are looking at</strong>, not the whole queue — so a name that is not on this page will
              not be found by typing it. The stage chips <em>do</em> filter the whole queue. There
              is no &ldquo;all statuses&rdquo; view: with no chip selected you get the working
              queue, which is everything awaiting a decision plus everything risk is holding.
              &ldquo;Awaiting maker&rdquo; and &ldquo;Awaiting checker&rdquo; are the same status
              underneath, split here by whether anyone has approved it yet. The{' '}
              <strong>Ratio</strong> column is recalculated fresh on every read from the
              player&rsquo;s lifetime totals — it is not a snapshot taken when the request was made
              — and it is banded against the hold threshold each brand can set for itself:{' '}
              {ratioBandingNote}
            </div>
          </div>
        </Panel>
      )}

      {/* ── Deposits ───────────────────────────────────────────────────────── */}
      {tab === 'deposits' && (
        <Panel>
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              <strong>These are ledger rows, not deposit records.</strong> This screen cannot read
              the deposit records themselves yet, so it shows the ledger entries they produced. You
              can see the difference in the columns: the reference is the ledger&rsquo;s{' '}
              <code>TX-</code> number rather than the <code>DP-</code> deposit number, there is no
              provider reference, and the payment rail is not shown at all — the ledger does not
              record it. <strong>A deposit that never reached the ledger is not in this list</strong>
              , which means the ones that failed before posting are exactly the ones you cannot see
              here.
            </div>
          </div>

          {/* The settlement filter, server-side. Without it the six FAILED rows
              the Retry control exists for sit on pages 217-394 of the ledger,
              which is the same as not having the control at all. */}
          <div className="row" style={{ marginTop: 'var(--space-3)' }}>
            <Chips
              label="Filter deposits by settlement"
              active={depositFilter}
              onChange={(key) => {
                setDepositFilter(key);
                setDepositPage(1);
              }}
              options={DEPOSIT_FILTERS.map((f) => ({ key: f.key, label: f.label }))}
            />
          </div>

          {depositLoading && deposits.length === 0 ? (
            <div className="skeleton" style={{ height: '240px', marginTop: 'var(--space-3)' }}>
              &nbsp;
            </div>
          ) : (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <DataTable
                caption="Deposit-kind ledger transactions with player, amount, settlement status, provider error code and time"
                columns={depositColumns}
                rows={deposits}
                rowKey={(t) => t.id}
                empty={
                  depositError ? (
                    <EmptyState icon="warning-circle" title="Deposit activity could not be read">
                      The read failed, so this table is empty because we could not fetch it. It is
                      not a brand with no deposits.
                    </EmptyState>
                  ) : (
                    <EmptyState
                      icon="arrows-left-right"
                      title={
                        depositFilter === 'ALL'
                          ? `No deposits for ${brandLabel}`
                          : `No ${DEPOSIT_FILTERS.find((f) => f.key === depositFilter)!.label.toLowerCase()} deposits for ${brandLabel}`
                      }
                      action={<Button onClick={resetFilters}>Reset filters</Button>}
                    >
                      No deposit
                      {depositFilter === 'ALL' ? '' : ` with that settlement status`} is posted for
                      the brand you have selected. Deposits that never reached the ledger would not
                      appear here either — see the note above.
                    </EmptyState>
                  )
                }
              />
            </div>
          )}

          <div className="text-subtle" style={{ fontSize: '12px', paddingTop: 'var(--space-2)' }}>
            {depositMeta
              ? `${formatCount(deposits.length)} of ${formatCount(depositMeta.total)} deposit transactions${
                  depositFilter === 'ALL'
                    ? ''
                    : ` · ${DEPOSIT_FILTERS.find((f) => f.key === depositFilter)!.label.toLowerCase()} only, filtered server-side`
                }`
              : depositError
                ? 'the ledger read failed — there is no count to report'
                : 'counting deposit transactions…'}
          </div>

          {depositMeta && (
            <Pagination
              page={depositMeta.page}
              perPage={depositMeta.perPage}
              total={depositMeta.total}
              onPage={setDepositPage}
            />
          )}

          <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="info" />
            <div>
              <strong>Retry records an attempt. It does not re-send the deposit.</strong> Pressing
              Retry writes down who tried, why, which provider error the deposit had at the time,
              and which deposit it was against — all recorded together, so the attempt cannot exist
              without its reason. <strong>It does not contact the payment provider and no money
              moves.</strong> A second attempt on the same deposit within{' '}
              {DEPOSIT_RETRY_COOLDOWN_MINUTES} minutes is refused and names the attempt that
              already exists.{' '}
              <strong>
                It reaches only the failures that reached the ledger.
              </strong>{' '}
              An attempt is attached to a row in the table above, and a deposit that failed before
              posting has no row there — which is where most failures actually are. Those are out of
              reach from this tab.{' '}
              {errorCodeState === 'failed' ? (
                <>
                  <strong>
                    The provider&rsquo;s error-code list could not be read this time, so no row
                    below carries its meaning or its advice on whether a retry is safe.
                  </strong>{' '}
                  A code shown bare below is one nobody was able to look up — not one we have
                  nothing to say about.
                </>
              ) : errorCodeState === 'forbidden' ? (
                <>
                  Explaining these codes needs the <code>transactions.view</code> permission, and
                  your role, {role}, does not hold it — so the codes are shown bare rather than
                  explained.
                </>
              ) : (
                <>
                  Where a provider error code is present, the sentence beside it is the
                  provider&rsquo;s own explanation, including whether they consider a retry safe.
                </>
              )}
            </div>
          </div>
        </Panel>
      )}

      {/* ── Manual adjustments ─────────────────────────────────────────────── */}
      {tab === 'adjustments' && (
        <Panel>
          <div className="notice notice-quiet">
            <Icon name="shield-check" />
            <div>
              <strong>Two tables, because these are two different facts.</strong> The first is the
              queue: requests waiting for a verdict. No money has moved for any of them, so they
              cannot appear in the ledger at all. The second is what approving one produces — the
              actual movement. Approving posts the money and links it to the request together, so
              you can always get from one to the other.
            </div>
          </div>

          {/* ── The checker queue ──────────────────────────────────────────── */}
          <div style={{ marginTop: 'var(--space-3)' }}>
            <h4 style={{ margin: 0 }}>
              Awaiting a checker
              {pendingMeta ? ` · ${formatCount(pendingMeta.total)}` : ''}
            </h4>
            <div className="text-subtle" style={{ fontSize: '12px' }}>
              {pendingState === 'loading'
                ? 'reading the queue…'
                : pendingState === 'forbidden'
                  ? `${role} cannot read this queue, so it is unread rather than empty. Ask a Super Admin if you need it.`
                  : pendingState === 'failed'
                    ? 'that read failed — this is not a queue with nothing in it'
                    : `${formatCount(pending.length)} of ${formatCount(pendingMeta?.total ?? 0)} requests awaiting a verdict in ${brandLabel}`}
            </div>
          </div>

          {pendingState === 'loading' && pending.length === 0 ? (
            <div className="skeleton" style={{ height: '120px', marginTop: 'var(--space-3)' }}>
              &nbsp;
            </div>
          ) : (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <DataTable
                caption="Manual adjustments awaiting a checker, with player, stored direction, amount, reason code, maker and the approve or reject decision"
                columns={pendingColumns}
                rows={pending}
                rowKey={(a) => a.id}
                empty={
                  pendingState === 'failed' ? (
                    <EmptyState icon="warning-circle" title="The adjustment queue could not be read">
                      The read failed, so this table is empty because we could not fetch it — not
                      because nothing is awaiting a checker.
                    </EmptyState>
                  ) : pendingState === 'forbidden' ? (
                    <EmptyState icon="lock-simple" title="You cannot read this queue">
                      Reading this queue needs the <code>transactions.view</code> permission,
                      which {role} does not hold. Nothing was read, so nothing here tells you
                      anything about the queue.
                    </EmptyState>
                  ) : (
                    <EmptyState icon="check-circle" title={`Nothing awaits a checker in ${brandLabel}`}>
                      The read succeeded and nothing is awaiting a checker for this brand — a real
                      zero. Raise one with <strong>New adjustment</strong> above.
                    </EmptyState>
                  )
                }
              />
            </div>
          )}

          {pendingMeta && pendingMeta.total > pendingMeta.perPage && (
            <Pagination
              page={pendingMeta.page}
              perPage={pendingMeta.perPage}
              total={pendingMeta.total}
              onPage={setPendingPage}
            />
          )}

          {/* ── Already posted ─────────────────────────────────────────────── */}
          <div style={{ marginTop: 'var(--space-4)' }}>
            <h4 style={{ margin: 0 }}>Already posted to the ledger</h4>
            <div className="text-subtle" style={{ fontSize: '12px' }}>
              The money that approving an adjustment has actually moved.
            </div>
          </div>

          {adjustmentLoading && adjustments.length === 0 ? (
            <div className="skeleton" style={{ height: '200px', marginTop: 'var(--space-3)' }}>
              &nbsp;
            </div>
          ) : (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <DataTable
                caption="Adjustment-kind ledger transactions with player, direction taken from the entry sign, amount and posting status"
                columns={adjustmentColumns}
                rows={adjustments}
                rowKey={(t) => t.id}
                empty={
                  adjustmentError ? (
                    <EmptyState icon="warning-circle" title="Posted adjustments could not be read">
                      The read failed, so this table is empty because we could not fetch it. It is
                      not a brand with no posted adjustments.
                    </EmptyState>
                  ) : (
                    <EmptyState
                      icon="coins"
                      title={`No posted adjustments for ${brandLabel}`}
                      action={<Button onClick={resetFilters}>Reset filters</Button>}
                    >
                      No adjustment has been posted to the ledger for this brand.
                      This is <strong>not</strong> the checker queue — that is the table above.
                    </EmptyState>
                  )
                }
              />
            </div>
          )}

          <div className="text-subtle" style={{ fontSize: '12px', paddingTop: 'var(--space-2)' }}>
            {adjustmentMeta
              ? `${formatCount(adjustments.length)} of ${formatCount(adjustmentMeta.total)} posted adjustments`
              : adjustmentError
                ? 'the ledger read failed — there is no count to report'
                : 'counting posted adjustments…'}
          </div>

          {adjustmentMeta && (
            <Pagination
              page={adjustmentMeta.page}
              perPage={adjustmentMeta.perPage}
              total={adjustmentMeta.total}
              onPage={setAdjustmentPage}
            />
          )}

          <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="info" />
            <div>
              <strong>Direction</strong> in this lower table is worked out from the money that
              actually moved, which is what a credit and a debit really are. The queue above can
              only show the direction that was <em>requested</em>, because nothing has moved for
              those rows yet. The reason and the person who raised it live on the request, not on
              the movement, so this table cannot show them.{' '}
              <strong>A checker can never be the maker.</strong> The screen hides the buttons on
              your own requests, the server refuses before writing anything, and the
              platform&rsquo;s own records refuse it underneath both — three layers, so no slip in any one of them can let
              one person do both halves.
            </div>
          </div>
        </Panel>
      )}

      {/* ── Payout accounts ────────────────────────────────────────────────── */}
      {/* Not wrapped in a Panel here: the tab draws its own, and its drawer must sit outside
          one (a panel's backdrop blur would trap a fixed-position drawer inside the panel). */}
      {tab === 'payout-accounts' && (
        <PayoutAccountsReview
          canDecide={canApprove}
          role={role}
          onCountChanged={setReviewWaiting}
        />
      )}

      {/* ── Parked notifications ───────────────────────────────────────────── */}
      {/* The payout desk's (ruling 173): read with the key this screen reads with and the brand
          chosen in the header, as the Payout provider tile's count is; marked seen with
          funds.approve. A mark reads the band again, so the tile's count follows. */}
      {tab === 'parked-notifications' && (
        <ParkedNotifications
          canRead={canReadLedger}
          canMark={canApprove}
          brandId={brandId}
          seesEveryBrand={(session?.brands.length ?? 0) === 0}
          role={role}
          onMarked={() => setBandReads((n) => n + 1)}
        />
      )}

      {/* ── Review drawer ──────────────────────────────────────────────────── */}
      <Drawer
        open={review !== null}
        onClose={closeReview}
        title={review?.ref ?? ''}
        subtitle={
          review ? `${review.playerName} · ${review.playerRef} · ${review.brandName}` : undefined
        }
        actions={
          review ? (
            <VerdictButtons
              row={review}
              canApprove={canApprove}
              isMine={reviewIsMine}
              deciding={deciding !== null}
              decided={decidedId === review.id}
              gapId="decision-gap"
              onReject={() => {
                setRejecting(review);
                setRejectReason(REJECTION_REASONS[0]!.code);
                setRejectNote('');
              }}
              onRelease={() => {
                /* Omitted rather than sent empty when nothing was typed: the
                   contract makes `note` optional, and an empty string in the
                   audit snapshot reads as a note somebody wrote and left
                   blank. */
                const trimmed = releaseNote.trim();
                void decide(review, 'release', trimmed ? { note: trimmed } : {});
              }}
            />
          ) : null
        }
      >
        {review && (
          <div className="stack">
            {/* The stage as the last read of this request has it: after any action taken from
                the drawer the row is read again, and the tag follows. */}
            <div className="row" aria-busy={reviewReading === 'reading'}>
              <StageTag row={review} />
              {hasBreachedSla(review) && <Tag tone="outline">SLA breached</Tag>}
              {reviewReading === 'reading' ? (
                <span className="text-subtle" style={{ fontSize: '11.5px' }}>
                  reading this request again…
                </span>
              ) : null}
            </div>
            {reviewReading === 'failed' ? (
              <div className="notice">
                <Icon name="warning-circle" />
                <div>
                  <strong>This request could not be read again after the last action</strong>, so
                  the stage and the buttons here are from before it.
                  {decidedId === review.id
                    ? ' Your verdict went through, so neither can be pressed again.'
                    : ''}{' '}
                  Close the drawer and open the request again to see where it stands.
                </div>
              </div>
            ) : null}

            {/* The amount card. */}
            <div className="stat">
              <div className="stat-label">Payout amount</div>
              <div className="stat-value lg tabular">{formatPesos(BigInt(review.amount))}</div>
              <div className="row" style={{ marginTop: 'var(--space-2)' }}>
                <span className="text-muted">
                  {review.method} ·{' '}
                  <span className="tabular">{review.railAccountMask ?? 'no mask stored'}</span>
                </span>
              </div>
              {/* WHAT IS KEPT OF THE NUMBER, said for the row in front of the
                  operator rather than for the newest rows only. A StarPay payout
                  is read from the vault at the hand-over and nowhere else; an
                  account on a rail that records is in the vault and never read;
                  and a request raised before registered accounts kept these four
                  digits and nothing more — the sentence this card said for every
                  row until the vault existed, still true of those. */}
              <div className="text-subtle" style={{ fontSize: '11.5px' }}>
                {review.sendable ? (
                  <>
                    The full number is encrypted in the account vault and is read by the platform
                    only at the moment it is handed to StarPay — every read is recorded. No screen
                    shows it, by design. The holder&rsquo;s name is in the checks below.
                  </>
                ) : review.payoutAccountStatus !== null ? (
                  <>
                    The full number is encrypted in the account vault. This rail records the payout
                    and a person sends it, so the platform never reads the number and no screen shows
                    it — confirm the full number with the player before sending. The holder&rsquo;s
                    name is in the checks below.
                  </>
                ) : (
                  <>
                    Raised before registered accounts: these four digits are all this platform kept,
                    so no screen and no report can show the rest. Confirm the full number with the
                    player before releasing.
                  </>
                )}
              </div>
              <div className="text-subtle" style={{ fontSize: '11.5px', marginTop: 'var(--space-2)' }}>
                Requested {formatWait(waitedFor(review))} ago ·{' '}
                {stamp.format(new Date(review.requestedAt))} · SLA {formatWait(slaMinutes)} from
                request to release, due {stamp.format(new Date(review.slaDueAt))}.
              </div>
            </div>

            <Panel title="Automated checks">
              <div className="text-subtle" style={{ fontSize: '11.5px', marginBottom: 'var(--space-2)' }}>
                Every check below is live; wagering and duplicates inform the decision and stop
                nothing.
              </div>
              <dl className="stack" style={{ margin: 0 }}>
                {checks.map((c) => (
                  <div className="def-row" key={c.key}>
                    <dt>
                      <Icon
                        name={CHECK_ICON[c.state]}
                        label={
                          c.state === 'ok' ? 'Passed' : c.state === 'attention' ? 'Attention' : 'Not checked'
                        }
                      />{' '}
                      {c.label}
                      {c.note ? (
                        <div className="text-subtle" style={{ fontSize: '11px', maxWidth: '46ch' }}>
                          {c.note}
                        </div>
                      ) : null}
                    </dt>
                    <dd>{c.value}</dd>
                  </div>
                ))}
              </dl>
            </Panel>

            {/* Where the money stands once released: StarPay's own fields, every
                hand-over, and the actions on a parked payout. Renders nothing for a
                request still waiting for a verdict. After an action the drawer reads
                its row again, so the stage above and the buttons below follow. The
                driver is whichever of the screen's two reads has named it — the
                provider's status or the summary — and null until one has. */}
            <PayoutPanel
              withdrawalId={review.id}
              row={review}
              canAct={canApprove}
              driver={provider?.driverName ?? summary?.payouts.provider ?? null}
              onChanged={() => void followReview(review)}
            />

            <Panel title="Player record">
              {detailLoading && !reviewPlayer ? (
                <div className="skeleton" style={{ height: '120px' }}>
                  &nbsp;
                </div>
              ) : detailError ? (
                <div className="notice">
                  <Icon name="warning-circle" />
                  <div>{detailError}</div>
                </div>
              ) : reviewPlayer ? (
                <dl className="stack" style={{ margin: 0 }}>
                  <div className="def-row">
                    <dt>Cash balance</dt>
                    <dd className="tabular">{formatPesos(BigInt(reviewPlayer.cash_balance))}</dd>
                  </div>
                  <div className="def-row">
                    <dt>Risk score</dt>
                    <dd>
                      {riskBand(reviewPlayer.risk_score)} {formatCount(reviewPlayer.risk_score)}
                    </dd>
                  </div>
                  <div className="def-row">
                    <dt>Account status</dt>
                    <dd>{STATUS_LABEL[reviewPlayer.status] ?? reviewPlayer.status}</dd>
                  </div>
                  <div className="def-row">
                    <dt>Tier</dt>
                    <dd>{TIER_LABEL[reviewPlayer.tier] ?? reviewPlayer.tier.replace('_', ' ')}</dd>
                  </div>
                  <div className="def-row">
                    <dt>Registered</dt>
                    <dd>{stamp.format(new Date(reviewPlayer.registered_at))}</dd>
                  </div>
                </dl>
              ) : null}
              <div className="text-subtle" style={{ fontSize: '11px', marginTop: 'var(--space-2)' }}>
                Read from the player&rsquo;s record. The cash balance is added up from the ledger
                each time it is asked for, not held as a stored figure that could drift.
              </div>
            </Panel>

            {/* The player's other requests and how each one ended — read when the drawer opens. */}
            <OtherRequests row={review} />

            {/* The maker line, which is the whole point of the drawer. */}
            <MakerCheckerLine row={review} sessionId={session?.id} canDecide={canApprove} />

            {/*
              The release note. It is stored, which is why it is offered.

              `POST /api/withdrawals/:id/release` parses `releaseWithdrawalRequest`
              (@dayon/contracts) and copies `note` into the after-snapshot of the
              WITHDRAWAL_RELEASED audit row, in the same transaction as the ledger
              movement. There is no `release_note` column on the withdrawal — the
              log is the store, and it is the store an auditor reads anyway.

              A REFUSAL is not typed here. It needs a reason code as well as a
              note and the note is mandatory, so the Reject request button opens
              the dialog that asks for both and writes `rejection_note`.
            */}
            <Field
              label="Release note (optional)"
              hint={
                !canApprove
                  ? `Releasing payouts is not something ${role} can do, so there is no release here for a note to belong to.`
                  : !reviewDecidable
                    ? 'This request is already decided. A note is recorded as part of the verdict that carries it; nothing on this platform adds one to a closed request afterwards.'
                    : reviewIsMine
                      ? 'You raised this request, so you cannot be the one to release it and there is no decision here for a note to attach to.'
                      : 'Up to 500 characters. Leave it blank and nothing is recorded — there is no empty note. What you write is kept in the audit trail with your name and the release, which is the only place it lives, so it cannot be edited afterwards. Refusing asks for a note of its own, and that one is required.'
              }
            >
              {({ id, describedBy }) => (
                <textarea
                  id={id}
                  className="input"
                  rows={3}
                  maxLength={500}
                  disabled={!reviewDecidable || reviewIsMine || deciding !== null}
                  aria-describedby={describedBy}
                  value={releaseNote}
                  onChange={(e) => setReleaseNote(e.target.value)}
                  placeholder="Reference the checks above"
                />
              )}
            </Field>

            {reviewIsMine ? (
              <div className="notice" id="decision-gap">
                <Icon name="lock-simple" />
                <div>
                  {/* Same rewrite as the panel above, for the same reason: an
                      operator is told WHAT they cannot do and WHO can, not the
                      name of the rule that stops them. It is said here, before
                      they write a note and press a button, so the refusal does
                      not arrive at the end of the work. */}
                  <strong>You raised this one, so you cannot decide it.</strong> Someone else has
                  to — anybody who can release payouts, as long as it is not you. Told you now
                  rather than after you have written your note.
                </div>
              </div>
            ) : !canApprove ? (
              /*
               * A READER, NOT A CHECKER — and this branch only became reachable
               * when the module gate moved from `funds.approve` to
               * `transactions.view` so Risk Officers and Auditors could read
               * the queue at all.
               *
               * Without it they would fall into the branch below and be told a
               * still-open request had "already been decided", which is a
               * different and false statement. Two reasons a verdict is
               * unavailable need two notices; one notice covering both is one
               * of them lying.
               */
              <div className="notice" id="decision-gap">
                <Icon name="lock-simple" />
                <div>
                  <strong>You can read this queue but not decide it.</strong> A verdict needs the{' '}
                  <code>funds.approve</code> capability and your role, {role}, does not hold it.
                  Nothing here is hidden from you — the request, its ratio, its checks and its audit
                  trail all read the same as they would for a checker.
                </div>
              </div>
            ) : !reviewDecidable ? (
              <div className="notice" id="decision-gap">
                <Icon name="lock-simple" />
                <div>
                  <strong>This request has already been decided.</strong>{' '}
                  {/* Until the row read again lands, its status is the one from before the verdict
                      given here: said as the verdict, never as a status it no longer has. */}
                  {review.status === 'AWAITING_CHECKER' || review.status === 'HELD_BY_RISK'
                    ? 'Your verdict on it went through'
                    : `It is ${review.status.toLowerCase().replace(/_/g, ' ')}`}
                  , and a verdict is given once: re-deciding would overwrite the first one and the
                  person who made it. The audit trail carries who decided it and when.
                </div>
              </div>
            ) : (
              <div className="notice notice-quiet">
                <Icon name="info" />
                <div>
                  Releasing takes the money off the account and puts your name on it, together —
                  the payment and the record of who made it cannot come apart. The balance is
                  checked at that moment rather than when the player asked, so a request that has
                  outgrown the account is refused instead of overdrawing it. Refusing gives nothing
                  back, because nothing was taken while it waited.
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* ── Fund audit trail ───────────────────────────────────────────────── */}
      <Dialog
        open={dialog === 'audit'}
        onClose={() => setDialog(null)}
        title="Fund management audit trail"
        wide
        actions={
          <Button variant="primary" onClick={() => setDialog(null)}>
            Close
          </Button>
        }
      >
        <div className="stack">
          <p className="text-muted" style={{ margin: 0, fontSize: '13px' }}>
            <strong>This log can only be added to.</strong> The platform&rsquo;s own records refuse
            to change or delete an entry, and each one carries a fingerprint of the one before it — so
            removing or editing any entry breaks the chain visibly. The read below checks that chain
            and tells you if it is broken. It is <strong>not</strong> a record of every change on
            the platform; most of this console still cannot change anything. But every fund verdict
            is here: releasing or refusing a payout, confirming a parked payout paid, refunding it,
            re-sending it under the same id or closing its hold, accepting or refusing a payout
            account, and raising, approving or refusing a manual adjustment, each recorded together with the
            change itself, so an action with no entry is not a state this platform can reach.
            Every row carries who, what, which player, which brand, when, and a before-and-after.
            The reason you typed is inside that before-and-after rather than in a column of its
            own.
          </p>

          {auditLoading && !audit ? (
            <div className="skeleton" style={{ height: '200px' }}>
              &nbsp;
            </div>
          ) : (
            <>
              <DataTable
                caption="Audit entries whose subject is a withdrawal, deposit, deposit retry attempt, manual adjustment, ledger transaction, payout account, StarPay notification or the payout provider"
                columns={FUND_TRAIL_COLUMNS}
                rows={fundAuditRows}
                rowKey={(e) => e.seq}
                empty={
                  auditFailed ? (
                    /* The read failed. Nothing here is a statement about the
                       trail — the trail was never seen. */
                    <EmptyState icon="warning-circle" title="The audit trail could not be read">
                      The read failed, so this table is empty because we could not fetch it — not
                      because no fund event has been recorded, and not because the log is empty.
                      Nothing here counts anything, and <strong>the chain was not checked</strong>.
                      The reason is in the message that just opened; close and reopen this trail to
                      try again.
                    </EmptyState>
                  ) : (
                    <EmptyState
                      icon="receipt"
                      title="No fund event in the entries this read covers"
                    >
                      {/* NOT "no fund event has ever been audited", which is
                          what this said while the fund path could not write at
                          all. It can now — a release, a rejection, an
                          adjustment verdict and a recorded retry attempt each
                          leave a row — so the sentence had become a claim about
                          a trail this dialog never saw. */}
                      None of the {formatCount(audit?.entries.length ?? 0)} entries read here
                      concerns a withdrawal, deposit or adjustment.{' '}
                      <strong>That is a statement about this page, not about the trail.</strong>{' '}
                      {trailBrands === null ? (
                        <>
                          This reads the most recent entries of the whole log, of every kind, so a
                          fund verdict older than those is simply not shown here
                        </>
                      ) : (
                        <>
                          This reads the most recent entries recorded against{' '}
                          {trailBrands.join(', ')}, of every kind, so a fund verdict older than
                          those, or one recorded against another brand or against no brand, is
                          simply not shown here
                        </>
                      )}{' '}
                      — its absence is not a claim that it never happened. The chain verdict below
                      covers the whole log; this table does not.
                    </EmptyState>
                  )
                }
              />

              {audit && (
                <div className={audit.intact ? 'notice notice-quiet' : 'notice'}>
                  <Icon name={audit.intact ? 'shield-check' : 'warning-circle'} />
                  <div>
                    {audit.intact ? (
                      <>
                        <strong>Chain verified intact across the whole log.</strong> The{' '}
                        {formatCount(audit.entries.length)} most recent entries
                        {trailBrands === null
                          ? ''
                          : ` recorded against ${trailBrands.join(', ')}`}{' '}
                        were read; this table shows the {formatCount(fundAuditRows.length)} of them
                        with a fund subject.
                      </>
                    ) : (
                      <>
                        <strong>
                          The audit chain reports {formatCount(audit.chainBreaks.length)} integrity
                          finding(s).
                        </strong>{' '}
                        A break means rows were altered after they were written. Escalate before
                        relying on anything in this trail.
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="notice notice-quiet">
                <Icon name="info" />
                <div>
                  Every change on this screen is recorded together with the change itself —
                  releasing, rejecting, confirming a parked payout paid, refunding it, re-sending it
                  under the same id, closing its hold, raising an adjustment, approving or rejecting
                  one, recording a deposit retry, restarting payouts, accepting or refusing a payout
                  account, and marking a StarPay notification seen.{' '}
                  {trailBrands === null ? (
                    <strong>The trail reads the 100 newest entries of the whole log</strong>
                  ) : (
                    <strong>
                      The trail reads the 100 newest entries recorded against{' '}
                      {trailBrands.join(', ')}
                    </strong>
                  )}
                  , of <em>every</em> kind, and filters them to fund events here in your browser —
                  so a busy day of other activity can push an older fund event out of reach of this
                  view, and a change missing from it is not a change nobody made.{' '}
                  {trailBrands !== null && (
                    <>
                      Your access is restricted to {trailBrands.join(', ')}, so an entry recorded
                      against any other brand is not read here, and neither is one recorded against
                      no brand — restarting payouts, for one, covers every brand and is recorded
                      against none.{' '}
                    </>
                  )}
                  Nothing on the platform enforces a retention period, and there is no export from
                  here.
                </div>
              </div>
            </>
          )}
        </div>
      </Dialog>

      {/* ── Refusing a withdrawal ──────────────────────────────────────────── */}
      {/*
        NOT DISMISSIBLE, AND THE GUARD IS ON THIS SIDE OF THE BUTTON ON PURPOSE.

        A stray Escape here does not interrupt a write — it throws away a
        decision somebody opened this dialog to make, together with the reason
        code and the note they had already typed, and says nothing about having
        done it. That is the moment worth guarding, which is why this is `false`
        outright rather than `{!deciding}`: a guard that only holds while the
        request is in flight is a guard over the one moment when the verdict has
        already been given, and it leaves the moment before the press — the one
        that matters — wide open. Cancel is two inches away and names itself.
      */}
      <Dialog
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title={rejecting ? `Refuse ${rejecting.ref}?` : 'Refuse withdrawal'}
        dismissible={false}
        actions={
          <>
            <Button onClick={() => setRejecting(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={rejectNote.trim().length < 10 || deciding !== null}
              onClick={() => {
                if (rejecting) {
                  void decide(rejecting, 'reject', {
                    reasonCode: rejectReason,
                    note: rejectNote.trim(),
                  });
                }
              }}
            >
              {deciding ? 'Recording…' : 'Refuse the request'}
            </Button>
          </>
        }
      >
        <div className="stack">
          {/*
            DANGER RATHER THAN WARN, and it is the verdict that earns it rather
            than the money. No balance moves either way — the first two
            sentences are the ones this block has always carried, kept word for
            word, because "nothing comes back because nothing was taken" is the
            thing an operator most often has backwards. What makes it the
            heavier tone is that a request can be decided exactly once: a second
            verdict on a closed request is refused, so there is no screen on
            this platform that takes a refusal back, and the only route to a
            payout afterwards is a fresh request from the player.

            The last sentence is here, above the fields, because it is the half
            an operator would otherwise learn from a support ticket. The player
            is not emailed and nothing is sent to them; they read it in their
            own account, where the note typed below is printed verbatim and
            labelled as what the operator wrote — except on suspected fraud,
            which the player app deliberately prints without the note, so that a
            refusal cannot tell somebody what a review found. The Reason hint
            below says the code drives what the player is told; this says that
            the note does too.
          */}
          <div className="notice notice-danger">
            <Icon name="warning" />
            <div>
              Nothing is returned to the player because nothing was taken: the queue holds the
              request, it does not debit the balance. Refusing records the reason against{' '}
              {rejecting?.ref ?? 'the request'} and leaves{' '}
              {rejecting ? formatPesos(BigInt(rejecting.amount)) : 'the amount'} where it is. The
              player reads the refusal in their own account, and your note with it word for word —
              unless the reason is suspected fraud, where it is held back from them. A verdict is
              given once: nobody can reopen this request, so to be paid the player would have to
              ask again.
            </div>
          </div>

          <Field label="Reason" hint="Drives what the player is told, and how refusals are counted.">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              >
                {REJECTION_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            hint="At least 10 characters. A code on its own reads as a form number; the note is what tells the player something they can act on."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Payout account name does not match the KYC record."
              />
            )}
          </Field>
        </div>
      </Dialog>

      {/* ── Recording a deposit retry attempt ──────────────────────────────── */}
      <Dialog
        open={retrying !== null}
        onClose={() => setRetrying(null)}
        title={retrying ? `Record a retry attempt on ${retrying.ref}?` : 'Record a retry attempt'}
        actions={
          <>
            <Button onClick={() => setRetrying(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={retryNote.trim().length < 10 || recordingRetry}
              onClick={() => {
                if (retrying) void recordRetry(retrying, retryReason, retryNote.trim());
              }}
            >
              {recordingRetry ? 'Recording…' : 'Record the attempt'}
            </Button>
          </>
        }
      >
        <div className="stack">
          {/* The split, stated ABOVE the fields, so nobody submits this
              expecting the deposit to have been re-sent. */}
          <div className="notice" id="retry-gap">
            <Icon name="shield-check" />
            <div>
              <strong>This records an attempt. It does not re-send the deposit.</strong> It writes
              down you as the operator, the reason and note below, and the provider error this
              deposit carried at this moment — all recorded together, so the attempt cannot exist
              without its reason. <strong>The payment provider is not contacted, the ledger is
              untouched, and the player&rsquo;s balance does not change.</strong>{' '}
              {retrying && retrying.providerErrorCode ? (
                <>
                  This deposit failed with <code>{retrying.providerErrorCode}</code>
                  {(() => {
                    const meaning = errorMeaning(retrying.providerErrorCode);
                    if (!meaning) {
                      return errorCodeState === 'failed'
                        ? ' — and whether that code is retry-safe is unknown, because the provider code list could not be read.'
                        : '.';
                    }
                    return meaning.retry_safe
                      ? ` — ${meaning.meaning}. The provider marks this code retry-safe.`
                      : ` — ${meaning.meaning}. The provider marks this code DO NOT RETRY; recording an attempt against it is a decision an auditor will read.`;
                  })()}
                </>
              ) : null}
            </div>
          </div>

          <Field
            label="Reason"
            hint="A closed set, so a report can count why deposits are retried. None of them says the money moved."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={retryReason}
                onChange={(e) => setRetryReason(e.target.value)}
              >
                {RETRY_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            hint="At least 10 characters — anything shorter is refused. Somebody reads this years later, and a code on its own tells them nothing."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={retryNote}
                onChange={(e) => setRetryNote(e.target.value)}
                placeholder="Provider statement shows the debit; raising with Xendit support under ticket 4471."
              />
            )}
          </Field>

          {retrying && (retryHistory.get(retrying.id) ?? []).length > 0 && (
            <div className="notice notice-quiet">
              <Icon name="receipt" />
              <div>
                <strong>
                  {formatCount((retryHistory.get(retrying.id) ?? []).length)} attempt
                  {(retryHistory.get(retrying.id) ?? []).length === 1 ? '' : 's'} already recorded.
                </strong>{' '}
                Read them before adding another — attempts on one deposit are{' '}
                {DEPOSIT_RETRY_COOLDOWN_MINUTES} minutes apart and a closer one is refused.
                <ul>
                  {(retryHistory.get(retrying.id) ?? []).map((a) => (
                    <li key={a.id}>
                      <span className="tabular">{a.ref}</span> ·{' '}
                      {stamp.format(new Date(a.createdAt))} ·{' '}
                      {a.requestedByLabel ?? 'an admin with no name on file'} ·{' '}
                      {RETRY_REASONS.find((r) => r.code === a.reasonCode)?.label ?? a.reasonCode} —{' '}
                      {a.note}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* ── Refusing an adjustment ─────────────────────────────────────────── */}
      {/*
        Not dismissible, for the same reason as the withdrawal refusal above: an
        Escape struck by accident would drop the reason and the note without a
        word, and the press is the point at which this becomes irreversible, so
        the dialog is held open until the operator answers it one way or the
        other rather than only while the write is in flight.
      */}
      <Dialog
        open={rejectingAdjustment !== null}
        onClose={() => setRejectingAdjustment(null)}
        title={rejectingAdjustment ? `Refuse ${rejectingAdjustment.ref}?` : 'Refuse adjustment'}
        dismissible={false}
        actions={
          <>
            <Button onClick={() => setRejectingAdjustment(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={adjustRejectNote.trim().length < 10 || decidingAdjustment !== null}
              onClick={() => {
                if (rejectingAdjustment) {
                  void decideAdjustment(rejectingAdjustment, 'reject', {
                    reasonCode: adjustRejectReason,
                    note: adjustRejectNote.trim(),
                  });
                }
              }}
            >
              {decidingAdjustment ? 'Recording…' : 'Refuse the request'}
            </Button>
          </>
        }
      >
        <div className="stack">
          {/*
            WARN AND NOT DANGER, WHICH IS A JUDGEMENT AND NOT A DEFAULT — argue
            with it here if it is wrong, because the colours only mean something
            while they still differ from each other.

            The refusal above reaches a player, ends their payout and cannot be
            taken back by anyone; that one is the danger on this screen. This
            one moves nothing at all: no balance changes, no entry is reversed,
            no figure on any report moves, and nobody outside this console hears
            about it — a player is never told an adjustment was asked for, so
            they are never told one was refused. What closes for good is the
            REQUEST, and the way back is not a repair but a re-raise: whoever
            raised this can raise a fresh one for the same player and the same
            amount, and nothing refuses them for having been refused once. A
            consequence with a way back is what the warn tone is for.

            The wording of the first three sentences is unchanged — it was
            already the clearest statement in this dialog of what refusing does
            not do. The sentences after it say where the explanation goes, since
            nothing on this platform delivers it: the row simply leaves the
            queue, and the Note hint below promises the maker reads it, which is
            true only because the trail keeps it.
          */}
          <div className="notice notice-warn">
            <Icon name="warning" />
            <div>
              No balance changes and no entry is reversed, because raising this took nothing:{' '}
              {rejectingAdjustment?.ref ?? 'the request'} is a request for{' '}
              {rejectingAdjustment ? formatPesos(BigInt(rejectingAdjustment.amount)) : 'an amount'}{' '}
              that was never posted. Refusing records the reason against it and closes it for good —
              a verdict is given once. It then leaves this queue and appears in no other list here:
              nothing is sent to the maker, and your reason and note wait for them in the fund audit
              trail. If the correction is still the right one, somebody has to raise it again.
            </div>
          </div>

          <Field
            label="Reason"
            hint="A fixed list, so refusals can be counted by cause rather than read one by one. Your own words go after it, and the cause stays at the front so it can still be grouped."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={adjustRejectReason}
                onChange={(e) => setAdjustRejectReason(e.target.value)}
              >
                {ADJUSTMENT_REJECTION_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            hint="At least 10 characters. The maker reads this, so a code on its own is not enough."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={adjustRejectNote}
                onChange={(e) => setAdjustRejectNote(e.target.value)}
                placeholder="No ticket attached and the deposit settled normally."
              />
            )}
          </Field>
        </div>
      </Dialog>

      {/* ── Manual balance adjustment ──────────────────────────────────────── */}
      <Dialog
        open={dialog === 'adjust'}
        onClose={() => setDialog(null)}
        title="Manual balance adjustment"
        wide
        actions={
          <>
            <Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={raising}
              aria-describedby="adjust-gap"
              onClick={() => void raiseAdjustment()}
            >
              {raising ? 'Submitting…' : 'Submit for approval'}
            </Button>
          </>
        }
      >
        <div className="stack">
          {/* What this form does, above the fields, so nobody submits it
              expecting the player to have been paid. */}
          <div className="notice" id="adjust-gap">
            <Icon name="shield-check" />
            <div>
              <strong>Submitting raises a request. It moves no money.</strong> It records the
              request as awaiting a checker, with you as the maker. The player&rsquo;s balance
              changes only when a second admin approves it on the <strong>Manual adjustments</strong>{' '}
              tab — approving is what actually posts the money and links it back to this request.
            </div>
          </div>

          <div className="notice notice-quiet">
            <Icon name="shield-check" />
            <div>
              Maker-checker: you submit, a second approver confirms. Above ₱50,000 the checker must
              hold Finance Lead. Nothing moves until approval.{' '}
              <em>
                The ₱50,000 tier is written policy with nothing behind it — the platform stores no
                such threshold anywhere, so this screen does not enforce a tier it cannot read.{' '}
                <strong>Check it by hand.</strong> The part that IS enforced, by the
                platform&rsquo;s own records, is that the maker can never be the checker.
              </em>
            </div>
          </div>

          <div className="text-muted" style={{ fontSize: '13px' }}>
            {chosenPlayer
              ? `${chosenPlayer.name} · ${chosenPlayer.ref} · ${chosenPlayer.brandName} · cash ${formatPesos(BigInt(chosenPlayer.cashBalance))}`
              : playerState === 'ready' && playerOptions.length > 0
                ? 'No player selected yet — choose one below.'
                : 'No player selected. The note under the Player field says why the list is empty.'}
          </div>

          <Field
            label="Find a player"
            hint={
              canReadPlayers
                ? 'Searches player ID, name, email, phone and agent code across the whole roster, so somebody who is not on the page below can still be found.'
                : `Off, because ${role} cannot read the player list.`
            }
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={playerSearch}
                disabled={!canReadPlayers}
                onChange={(e) => setPlayerSearch(e.target.value)}
                placeholder="Player ID, name, email or phone"
              />
            )}
          </Field>

          <Field label="Player" hint={playerHint} error={adjustFields['playerId']}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={adjustPlayer}
                // Nothing to choose from is not a working control. The hint
                // beneath names which of the four reasons this is.
                disabled={playerState !== 'ready' || playerOptions.length === 0}
                onChange={(e) => setAdjustPlayer(e.target.value)}
              >
                <option value="">Select a player…</option>
                {playerOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {p.ref}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <div className="halves">
            <Field
              label="Direction"
              hint="Credit adds to the player's cash; debit takes from it. The direction carries the sign — the amount is always positive."
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={adjustDirection}
                  onChange={(e) => setAdjustDirection(e.target.value === 'DEBIT' ? 'DEBIT' : 'CREDIT')}
                >
                  <option value="CREDIT">Credit</option>
                  <option value="DEBIT">Debit</option>
                </Select>
              )}
            </Field>

            <Field
              label="Amount (PHP)"
              error={adjustFields['amount']}
              hint={
                adjustDirection === 'DEBIT' && chosenPlayer
                  ? `Kept as a whole number of centavos, so it is never rounded. ${chosenPlayer.name} holds ${formatPesos(BigInt(chosenPlayer.cashBalance))} — a debit cannot take an account below zero.`
                  : 'Kept as a whole number of centavos, so it is never rounded.'
              }
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  inputMode="decimal"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(e.target.value)}
                  placeholder="0.00"
                />
              )}
            </Field>
          </div>

          <Field
            label="Reason code"
            error={adjustFields['reasonCode']}
            hint="A closed set: the code is what a finance report groups refusals and goodwill by, and prose cannot be grouped. Nothing underneath checks the stored value, so this list, and the same list checked when the adjustment is saved, are the only thing keeping it to these five choices."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
              >
                {REASON_CODES.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Internal note"
            error={adjustFields['note']}
            hint="At least 10 characters, checked when it is saved. It is kept with the adjustment and copied into the audit log — a code on its own reads as a form number to the admin who has to approve this."
          >
            {({ id, describedBy }) => (
              <textarea
                id={id}
                className="input"
                rows={3}
                value={adjustNote}
                aria-describedby={describedBy}
                onChange={(e) => setAdjustNote(e.target.value)}
                placeholder="Ticket reference and what happened"
              />
            )}
          </Field>
        </div>
      </Dialog>

      {/* Signing as maker. The dialog exists to state the consequence BEFORE the
          press: the control reads like an approval and removes the caller's own
          ability to release every row in the selection. */}
      <Dialog
        open={takingAsMaker}
        onClose={() => setTakingAsMaker(false)}
        title={`Sign ${formatCount(takeableIds.length)} ${takeableIds.length === 1 ? 'request' : 'requests'} as maker?`}
        actions={
          <>
            <Button onClick={() => setTakingAsMaker(false)}>Cancel</Button>
            <Button
              variant="primary"
              busy={takingMaker}
              disabled={takingMaker || makerNote.trim().length < 10}
              onClick={() => void takeAsMaker()}
            >
              Sign as maker
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              <strong>This does not approve a payout, and it is not a step towards one.</strong> It
              records that you have reviewed{' '}
              {takeableIds.length === 1 ? 'this request' : 'these requests'} as the maker — and a
              payout&rsquo;s maker can never release it. After this you will{' '}
              <strong>not be able to release {takeableIds.length === 1 ? 'it' : 'any of them'}</strong>
              ; a different admin holding <span className="signin-perm">funds.approve</span> has to.
              <br />
              Nothing moves, no money is posted, and every player still waits — what changes is who
              can pay them.
            </div>
          </div>

          <div className="notice notice-quiet">
            <Icon name="coins" />
            <div>
              {formatCount(takeableIds.length)}{' '}
              {takeableIds.length === 1 ? 'request' : 'requests'} totalling{' '}
              <span className="tabular">
                {formatPesos(
                  selectedRows
                    .filter((w) => takeableIds.includes(w.id))
                    .reduce((sum, w) => sum + BigInt(w.amount), 0n),
                )}
              </span>
              . Each gets its own entry in the audit trail, naming you and this note, so a single
              payout can be traced without the others.
            </div>
          </div>

          <Field
            label="What did you check?"
            {...(makerNoteError ? { error: makerNoteError } : {})}
            hint="At least 10 characters, and the only free-text record of the review. Unlike every other write on this screen there is no reason code, because the reason is always the same — what an auditor asks is what you looked at."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={makerNote}
                onChange={(e) => setMakerNote(e.target.value)}
                placeholder="KYC verified, payout ratio under the hold, no open alerts."
              />
            )}
          </Field>
        </div>
      </Dialog>

    </>
  );
}
